import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  Tab,
  Nav,
  Row,
  Col,
  Form,
  Spinner,
  Accordion,
  AccordionButton,
  ProgressBar,
} from 'react-bootstrap'
import ApptToothModal from '@components/layout/Modals/Appointment/ApptTooth.modal'
import {
  IToothDetails,
  PatientWithInsurance,
  Procedure,
} from '@dataTypes/appointment.type'
import PlanActionMenuButton, {
  type PlanProviderMenuAction,
} from './PlanActionMenuButton/PlanActionMenuButton.component'
import PhaseActionMenuButton from './PhaseActionMenuButton/PhaseActionMenuButton.component'
import TxPlanStatusDropdown from './TxPlanStatusDropdown'
import {
  deleteTreatmentPlan,
  updateTreatmentPlan,
  addTreatmentPlan,
} from '@store/actions/treatment-plan.action'
import {
  IPhases,
  ITreatmentPlan,
  TxPlanStatus,
  IPhaseItems,
  IProcedureDentalCondition,
  TX_PLAN_STATUS,
} from '@dataTypes/treatmentPlan.types'
import ChartingDeleteItemModal from '@components/pages/Charting/Modals/ChartingDeleteItemModal.component'
import ChartingDiscardPlanChangesModal from '@components/pages/Charting/Modals/ChartingDiscardPlanChangesModal.component'
import ChartingChangePhaseStatusModal from '@components/pages/Charting/Modals/ChartingChangePhaseStatusModal.component'
import {
  fetchInsurancePlansByPatientId,
  getAllDiscount,
  fetchAllProcedures,
  fetchAllProcedureSettings,
} from '@store/actions'
import {
  clearLatestUpdatedTreatmentPlanId,
  clearAllChartingData,
} from '@store/reducers/charting.reducer'
import { formatNumber } from '@utils/numberFormatter.util'
import Button from '@components/common/Button/Button.component'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { Provider } from '@dataTypes/provider.types'
import { isTreatmentPlanProcedureProviderRole } from '@utils/providerRole.util'
import { Discount } from '@dataTypes/discount.type'
import { SettingStatus } from '@dataTypes/settingStatus.type'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createFailedToCreateTreatmentPlanToast,
  createFailedToDeleteTreatmentPlanToast,
  createFailedToUpdateTreatmentPlanToast,
  createTreatmentPlanCreatedToast,
  createTreatmentPlanDeletedToast,
  createTreatmentPlanUpdatedToast,
} from '@utils/notificationToast.util'
import AddTxPlanProcedureModal from '../Modals/AddTxPlanProcedureModal.component'
import EmptyTreatmentPlan from './EmptyState/EmptyTreatmentPlan.component'
import './TreatmentPlanMain.css'
import TxPlanPhaseProcedureGrid, {
  type TxPlanPhaseProcedureGridInteractionRef,
  type TxPlanProcedureFeeChangeArgs,
} from './TxPlanPhaseProcedureGrid.component'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import TxPlanPhaseAddProcedureRow from './TxPlanAddRow/TxPlanPhaseAddProcedureRow.component'
import TxPlanAddPhaseRow from './TxPlanAddRow/TxPlanAddPhaseRow.component'
import TreatmentPlanDentalChartPanel from './TreatmentPlanDentalChartPanel.component'
import TxPlanToothPhasePicker, {
  getVisibleTxPlanPhasePickerOptions,
} from './TxPlanToothPhasePicker/TxPlanToothPhasePicker.component'
import { useChartXraySplit } from '../Dental/useChartXraySplit.hook'
import {
  TP_CHART_PANE_MIN_PX,
  TP_CHART_VERTICAL_HANDLE_PX,
  useTreatmentPlanChartVerticalSplit,
} from './useTreatmentPlanChartVerticalSplit.hook'
import {
  adjustInsertBeforeVisibleForSamePhaseReorder,
  collectTxPlanMissingProviderRowKeys,
  getInsertBeforeVisibleIndexFromPointer,
  getVisibleItemIndex,
  movePhaseItem,
  parseTxPlanItemDragPayload,
  resolveTxPlanDiscountPercent,
  TX_PLAN_DISCOUNT_CUSTOM_ID,
  TX_PLAN_ITEM_DRAG_MIME,
  txPlanDiscountFormFromPlan,
  txPlanDiscountPayloadFromForm,
  txPlanHasVisiblePhaseMissingProcedure,
  resolveProcedureSurfaceSelectionRules,
  resolveProcedureDentalConditionsWithNestedCondition,
  collectTxPlanVisiblePhaseAccordionKeys,
  remapTxPlanAccordionKeys,
  resolveTxPlanPhaseIndexForProcedureShortcut,
  txPlanPhaseAccordionEventKey,
  txPlanPhaseHeaderKey,
} from '@utils/treatment-plan.utils'
import {
  collectPhaseToothSurfaceSiblings,
  mergePhaseItemsToToothSurfaceEntries,
} from '@utils/tooth-surface-edit.utils'
import {
  EMPTY_MISSING_PROVIDER_KEY_SET,
  EMPTY_PHASE_VALIDATION_KEY_SET,
} from './TreatmentPlanMain.constants'
import {
  calculateInsuranceForItem,
  convertToothDetailsToToothSurfaces,
  formatToothDetailsToString,
  generateDuplicatePlanName,
  generateNewTreatmentPlanName,
  getPhaseEditKey,
  groupToothSurfaceEntriesAsToothDetails,
  hasTxPlanFormChanges,
  applyClinicFeeToPhaseItem,
  recalculatePhaseItems,
  markHistoryAfterFormUpdate,
} from './treatmentPlanMain.helpers'
import type {
  PendingDelete,
  PendingDiscard,
  PendingStatusChange,
  TreatmentPlanHistoryActions,
  TreatmentPlanMainProps,
  TxPlanTabFormState,
} from './TreatmentPlanMain.types'
import TreatmentPlanProviderModals from './TreatmentPlanProviderModals.component'
import TxPlanVoiceStatus from './TxPlanVoiceStatus.component'
import { useTreatmentPlanHistory } from './useTreatmentPlanHistory.hook'
import { useTreatmentPlanPhases } from './useTreatmentPlanPhases.hook'
import { useTreatmentPlanProviders } from './useTreatmentPlanProviders.hook'
import { useTxPlanStreamingVoice } from '@hooks/useTxPlanStreamingVoice'
import { readTxPlanVoiceProcedure } from '@utils/tx-plan-voice-apply.utils'

import { logger } from '@utils/safeLogger'

const TreatmentPlanMain: React.FC<TreatmentPlanMainProps> = ({
  onViewNote,
  onAddTreatmentPlanRef,
  onEmptyPlansChange,
  navigationRef,
  isXrayPanelOpen,
  initialEmbeddedFiles,
  isHostVisible = true,
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const treatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans,
  )
  const billingDiscounts = useAppSelector((state) => state.discount.discounts)
  const activeBillingDiscounts = useMemo((): Discount[] => {
    return billingDiscounts.filter((d) => d.status === SettingStatus.Enabled)
  }, [billingDiscounts])
  const latestUpdatedTreatmentPlanId = useAppSelector(
    (state) => state.charting.latestUpdatedTreatmentPlanId,
  )
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const staffListFromStore = useAppSelector((state) => state.provider.data)
  const staffList = useMemo(
    (): Provider[] => staffListFromStore ?? [],
    [staffListFromStore],
  )
  const providerList = useMemo((): Provider[] => {
    return staffList.filter((p) =>
      isTreatmentPlanProcedureProviderRole(p.work_role),
    )
  }, [staffList])
  const insuranceData = useAppSelector(
    (state) => state.insurance.data?.insurancePlans,
  )
  const insuranceList = useMemo(() => insuranceData ?? [], [insuranceData])
  const procedures = useAppSelector((state) => state.procedure.data)
  const procedureSettings = useAppSelector(
    (state) => state.charting.procedureSettings,
  )
  const procedureCatalogFetched = useAppSelector(
    (state) => state.charting.txPlanProcedureCatalogFetched,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { allowManualFeeOverride, showInsuranceEstimate } =
    useClinicChartingPreferences()

  // Filter out treatment plans where all phases are completed (they should only show in history tab)
  const activeTreatmentPlans = useMemo(() => {
    return treatmentPlans.filter((plan) => {
      if (!plan.phases || plan.phases.length === 0) return true // Include plans with no phases
      // Exclude plans where all phases are completed
      return !plan.phases.every(
        (phase) => phase.status === TX_PLAN_STATUS.COMPLETED,
      )
    })
  }, [treatmentPlans])

  const [activeTabKey, setActiveTabKey] = useState<string>('create-new')
  const [editModes, setEditModes] = useState<Record<string, boolean>>({})
  const [chartSelectedTeeth, setChartSelectedTeeth] = useState<
    Record<string, number[]>
  >({})
  const [pendingToothAdd, setPendingToothAdd] = useState<{
    tabKey: string
    toothNums: number[]
  } | null>(null)
  const [pendingStatusChange, setPendingStatusChange] =
    useState<PendingStatusChange | null>(null)
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null)
  const [pendingDiscard, setPendingDiscard] = useState<PendingDiscard | null>(
    null,
  )
  const [chartVisible, setChartVisible] = useState<boolean>(true)
  const [editingTabName, setEditingTabName] = useState<string | null>(null)
  const [editingTabValue, setEditingTabValue] = useState<string>('')
  const [localDrafts, setLocalDrafts] = useState<Record<string, boolean>>({})
  const pendingCreatedPlanIdRef = useRef<string | null>(null)
  const prevPatientIdRef = useRef<string | null>(null)
  const tabsScrollableRef = useRef<HTMLDivElement | null>(null)
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const isEmptyState =
    activeTreatmentPlans.length === 0 && Object.keys(localDrafts).length === 0
  const {
    chartPaneHeightPx,
    chartPaneRef,
    onHandleMouseDown: onChartVerticalHandleMouseDown,
  } = useTreatmentPlanChartVerticalSplit({
    isSplitActive: chartVisible,
    containerRef: mainContainerRef,
    isEmptyState,
  })
  const [isToothModalOpen, setIsToothModalOpen] = useState(false)
  const [selectedItemForToothEdit, setSelectedItemForToothEdit] = useState<{
    tabKey: string
    phaseIdx: number
    item: IPhaseItems
    siblingItemIds: string[]
  } | null>(null)
  const [activeDropPhaseOriginalIdx, setActiveDropPhaseOriginalIdx] = useState<
    number | null
  >(null)
  const [dropInsertBeforeVisibleIdx, setDropInsertBeforeVisibleIdx] = useState<
    number | null
  >(null)
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null)

  const phaseDragOverRafRef = useRef<number | null>(null)
  const phaseDragOverLatestRef = useRef<{
    originalIdx: number
    clientY: number
    el: HTMLElement
  } | null>(null)
  const phaseDragOverCommittedRef = useRef<{
    phase: number | null
    insert: number | null
  }>({ phase: null, insert: null })

  const phaseGridInteractionRef =
    useRef<TxPlanPhaseProcedureGridInteractionRef>({
      saveHistorySnapshot: (): void => {},
      editToothSelection: (): void => {},
      clearTreatmentPlanDragUi: (): void => {},
    })

  const showXraySplit = isXrayPanelOpen

  const {
    splitRef: tpXraySplitRootRef,
    chartPaneFraction: tpXrayPaneFraction,
    onHandleMouseDown: onTpXraySplitHandleMouseDown,
  } = useChartXraySplit(showXraySplit)

  const attachTpXraySplitRef = useCallback(
    (node: HTMLDivElement | null): void => {
      if (showXraySplit && node !== null) {
        tpXraySplitRootRef(node)
      } else {
        tpXraySplitRootRef(null)
      }
    },
    [showXraySplit, tpXraySplitRootRef],
  )

  // Form state for each tab
  const [formStates, setFormStates] = useState<
    Record<string, TxPlanTabFormState>
  >({})

  const formStatesRef = useRef(formStates)
  formStatesRef.current = formStates
  const localDraftsRef = useRef(localDrafts)
  localDraftsRef.current = localDrafts
  const editModesRef = useRef(editModes)
  editModesRef.current = editModes
  const treatmentPlansRef = useRef(treatmentPlans)
  treatmentPlansRef.current = treatmentPlans
  const historyActionsRef = useRef<TreatmentPlanHistoryActions>({
    queueHistorySnapshotBefore: (): void => {},
    markHistorySavePending: (): void => {},
    markHistoryInitPending: (): void => {},
    saveHistorySnapshot: (): void => {},
  })
  const toggleEditModeRef = useRef<(tabKey: string) => void>((tabKey) => {
    void tabKey
  })

  const {
    phaseVisibility,
    phaseVisibilityRef,
    setPhaseVisibility,
    pendingPhaseDelete,
    setPendingPhaseDelete,
    editingPhaseKey,
    editingPhaseValue,
    setEditingPhaseValue,
    handleAddPhase,
    openProcedureModal,
    handleAddProcedureFromTooth,
    handleAddNewPhaseFromToothPicker,
    handleAddPhaseAndOpenProcedureModal,
    beginToothDrivenProcedureAdd,
    handleAddProcedure,
    confirmDeletePhase,
    handlePhaseStatusChange,
    handleMenuSelect,
    togglePhaseVisibility,
    getPhaseVisibility,
    toggleAllPhaseAccordions,
    handleStartEditPhaseName,
    handleSavePhaseName,
    handleCancelEditPhaseName,
  } = useTreatmentPlanPhases({
    localDrafts,
    editModes,
    formStatesRef,
    setFormStates,
    setEditModes,
    setPendingStatusChange,
    setPendingToothAdd,
    historyActionsRef,
    toggleEditModeRef,
    dispatch,
    showToast,
    insuranceList,
    activeBillingDiscounts,
  })

  const {
    canUndo,
    canRedo,
    handleUndo,
    handleRedo,
    saveHistorySnapshot,
    queueHistorySnapshotBefore,
    markHistoryInitPending,
    markHistorySavePending,
    clearHistory,
    resetAllHistory,
  } = useTreatmentPlanHistory({
    formStates,
    formStatesRef,
    localDrafts,
    localDraftsRef,
    editModes,
    editModesRef,
    phaseVisibilityRef,
    setFormStates,
    setPhaseVisibility,
  })

  historyActionsRef.current = {
    queueHistorySnapshotBefore,
    markHistorySavePending,
    markHistoryInitPending,
    saveHistorySnapshot,
  }

  const activeVoiceForm = formStates[activeTabKey]
  const txPlanVoiceEditing =
    Boolean(localDrafts[activeTabKey]) || Boolean(editModes[activeTabKey])
  const txPlanVoiceEnabled =
    isHostVisible &&
    Boolean(selectedPatient?.patient_id) &&
    Boolean(selectedClinic?.clinic_id) &&
    !isEmptyState &&
    Boolean(activeVoiceForm) &&
    txPlanVoiceEditing
  const voiceProcedures = useMemo(
    () => procedureSettings.map((setting) => readTxPlanVoiceProcedure(setting)),
    [procedureSettings],
  )
  const handleVoiceForm = useCallback(
    (next: {
      phases: IPhases[]
      activeAccordionKeys: string[]
      phasesChanged: boolean
      closeProcedureModal?: boolean
    }): void => {
      const tabKey = activeTabKey
      const isLocalDraft = localDraftsRef.current[tabKey] || false
      const isEditMode = isLocalDraft || editModesRef.current[tabKey] || false
      if (next.phasesChanged) {
        if (!isLocalDraft && !isEditMode) {
          setEditModes((prev) => ({ ...prev, [tabKey]: true }))
          historyActionsRef.current.markHistoryInitPending(tabKey)
        }
        if (isEditMode || isLocalDraft) {
          historyActionsRef.current.queueHistorySnapshotBefore(tabKey)
        }
      }
      setFormStates((prev) => {
        const current = prev[tabKey]
        if (!current) return prev
        return {
          ...prev,
          [tabKey]: {
            ...current,
            phases: next.phases,
            activeAccordionKeys: next.activeAccordionKeys,
            procedureModal:
              next.phasesChanged || next.closeProcedureModal
                ? null
                : current.procedureModal,
          },
        }
      })
      if (next.phasesChanged) {
        markHistoryAfterFormUpdate(
          historyActionsRef.current.markHistorySavePending,
          isEditMode,
          isLocalDraft,
          tabKey,
        )
      }
    },
    [activeTabKey, setEditModes, setFormStates],
  )
  const handleVoiceStatus = useCallback(
    (pending: { phaseIndex: number; status: TxPlanStatus }): void => {
      const tabKey = activeTabKey
      const formState = formStatesRef.current[tabKey]
      const phase = formState?.phases[pending.phaseIndex]
      if (!formState || !phase) return
      const plan =
        treatmentPlansRef.current.find((item) => item.tp_id === tabKey) ?? null
      const isLocalDraft = localDraftsRef.current[tabKey] || false
      const isEditMode = isLocalDraft || editModesRef.current[tabKey] || false
      handlePhaseStatusChange(
        pending.status,
        tabKey,
        pending.phaseIndex,
        phase,
        plan,
        isEditMode,
      )
    },
    [activeTabKey, handlePhaseStatusChange],
  )
  const handleVoiceSearch = useCallback(
    (pending: { phaseIndex: number; query: string }): void => {
      openProcedureModal(activeTabKey, {
        phaseIndex: pending.phaseIndex,
        initialQuery: pending.query,
      })
    },
    [activeTabKey, openProcedureModal],
  )
  const txPlanVoice = useTxPlanStreamingVoice({
    enabled: txPlanVoiceEnabled,
    contextKey: `${selectedClinic?.clinic_id ?? ''}:${selectedPatient?.patient_id ?? ''}:${activeTabKey}`,
    chart: {
      patient_id: selectedPatient?.patient_id ?? '',
      clinic_id: selectedClinic?.clinic_id ?? '',
      tabKey: activeTabKey,
      phases: activeVoiceForm?.phases ?? [],
      activeAccordionKeys: activeVoiceForm?.activeAccordionKeys ?? [],
    },
    procedures: voiceProcedures,
    insurance: activeVoiceForm?.selectedInsurance ?? null,
    discountPercent: activeVoiceForm
      ? resolveTxPlanDiscountPercent(
          activeVoiceForm.selectedDiscountId,
          activeVoiceForm.customDiscount,
          billingDiscounts,
        )
      : 0,
    isEditing: txPlanVoiceEditing,
    onForm: handleVoiceForm,
    onPendingStatus: handleVoiceStatus,
    onSearch: handleVoiceSearch,
  })
  const txPlanMicOn = ['connecting', 'listening', 'processing'].includes(
    txPlanVoice.phase,
  )
  useEffect(() => {
    if (!txPlanVoiceEnabled) return
    if (!procedureCatalogFetched.settings) {
      void dispatch(fetchAllProcedureSettings(null))
    }
    if (!procedures || procedures.length === 0) {
      void dispatch(fetchAllProcedures(null))
    }
  }, [
    dispatch,
    procedureCatalogFetched.settings,
    procedures,
    txPlanVoiceEnabled,
  ])

  const {
    missingProviderRowKeysByTab,
    phaseValidationErrorKeysByTab,
    pendingBulkProvider,
    bulkProviderDontAskAgain,
    setBulkProviderDontAskAgain,
    planProviderAssignModal,
    clearPlanProvidersModal,
    menuFillProviderId,
    setMenuFillProviderId,
    clearPhaseValidationError,
    clearProviderValidationForTab,
    resetAllProviderState,
    handleProcedureProviderChange,
    providerMenuDisabled,
    handleOpenAssignMissingProviders,
    handleOpenReplaceAllProviders,
    handleOpenClearAllProviders,
    closePlanProviderAssignModal,
    confirmPlanProviderAssign,
    closeClearPlanProvidersModal,
    confirmClearPlanProviders,
    handleBulkProviderModalHide,
    handleBulkProviderOnlyThisProcedure,
    confirmBulkProviderApplyAll,
    validateProvidersAndPhasesBeforeSave,
  } = useTreatmentPlanProviders({
    activeTabKey,
    localDrafts,
    editModes,
    formStates,
    formStatesRef,
    providerList,
    staffList,
    setFormStates,
    queueHistorySnapshotBefore,
    markHistorySavePending,
    showToast,
  })

  const handleProviderMenuSelect = useCallback(
    (action: PlanProviderMenuAction, tabKey: string): void => {
      if (action === 'assign-missing') {
        handleOpenAssignMissingProviders(tabKey)
      } else if (action === 'replace-all') {
        handleOpenReplaceAllProviders(tabKey)
      } else {
        handleOpenClearAllProviders(tabKey)
      }
    },
    [
      handleOpenAssignMissingProviders,
      handleOpenClearAllProviders,
      handleOpenReplaceAllProviders,
    ],
  )

  const visibleSavedPlans = useMemo(() => {
    const hasLocalDrafts = Object.keys(localDrafts).length > 0
    if (!hasLocalDrafts) return activeTreatmentPlans
    return activeTreatmentPlans.filter((plan) =>
      Boolean(formStates[plan.tp_id]),
    )
  }, [activeTreatmentPlans, localDrafts, formStates])

  useEffect(() => {
    const currentPatientId = selectedPatient?.patient_id || null
    const prevPatientId = prevPatientIdRef.current

    // Check if patient ID has changed
    if (currentPatientId !== prevPatientId) {
      // Clear drafts and form states when patient changes
      if (prevPatientId !== null) {
        pendingCreatedPlanIdRef.current = null
        setLocalDrafts({})
        setFormStates({})
        setEditModes({})
        resetAllHistory()
        resetAllProviderState()
        setChartSelectedTeeth({})
        setPendingToothAdd(null)
        setActiveTabKey('')
      }
      // Update the ref to track the new patient ID
      prevPatientIdRef.current = currentPatientId
    }

    if (selectedPatient) {
      dispatch(fetchInsurancePlansByPatientId(selectedPatient.patient_id))
      dispatch(getAllDiscount())
      // Fetch procedures if not already loaded (they include dental conditions)
      if (procedures.length === 0) {
        dispatch(fetchAllProcedures(null))
      }
    } else {
      dispatch(clearAllChartingData())
      setActiveTabKey('')
      setEditModes({})
      setFormStates({})
      setLocalDrafts({})
      resetAllProviderState()
      setChartSelectedTeeth({})
      setPendingToothAdd(null)
      prevPatientIdRef.current = null
    }
  }, [
    selectedPatient,
    dispatch,
    procedures.length,
    resetAllHistory,
    resetAllProviderState,
  ])

  useEffect(() => {
    setPendingToothAdd(null)
  }, [activeTabKey])

  // Resolve active tab when plans/drafts load — do not depend on form edits or activeTabKey
  // or every keystroke can fight handleTabSelect (e.g. while creating a local draft).
  useEffect(() => {
    if (!selectedPatient) return

    if (latestUpdatedTreatmentPlanId && Object.keys(localDrafts).length === 0) {
      setActiveTabKey(latestUpdatedTreatmentPlanId)
      const timeoutId = window.setTimeout(() => {
        dispatch(clearLatestUpdatedTreatmentPlanId())
      }, 100)
      return (): void => {
        window.clearTimeout(timeoutId)
      }
    }

    if (
      visibleSavedPlans.length === 0 &&
      Object.keys(localDrafts).length === 0
    ) {
      setActiveTabKey('')
      return
    }

    setActiveTabKey((currentKey) => {
      const pendingCreatedPlanId = pendingCreatedPlanIdRef.current
      if (
        pendingCreatedPlanId &&
        formStatesRef.current[pendingCreatedPlanId] &&
        activeTreatmentPlans.some((p) => p.tp_id === pendingCreatedPlanId)
      ) {
        pendingCreatedPlanIdRef.current = null
        return pendingCreatedPlanId
      }
      if (
        currentKey &&
        (visibleSavedPlans.some((p) => p.tp_id === currentKey) ||
          localDrafts[currentKey])
      ) {
        return currentKey
      }
      if (latestUpdatedTreatmentPlanId) {
        return latestUpdatedTreatmentPlanId
      }
      if (
        currentKey &&
        currentKey.startsWith('draft-') &&
        !localDrafts[currentKey]
      ) {
        const promotedPlanId = [...activeTreatmentPlans]
          .reverse()
          .find((p) => Boolean(formStatesRef.current[p.tp_id]))?.tp_id
        if (promotedPlanId) return promotedPlanId
      }
      if (visibleSavedPlans.length > 0) {
        return visibleSavedPlans[0].tp_id
      }
      const draftKeys = Object.keys(localDrafts)
      if (draftKeys.length > 0) {
        return draftKeys[0]
      }
      return ''
    })
  }, [
    selectedPatient,
    visibleSavedPlans,
    activeTreatmentPlans,
    latestUpdatedTreatmentPlanId,
    localDrafts,
    dispatch,
  ])

  // Initialize and sync form state for existing plans
  useEffect(() => {
    if (!selectedPatient) return
    setFormStates((prev) => {
      const newFormStates: typeof prev = {}
      treatmentPlans.forEach((plan) => {
        const tabKey = plan.tp_id
        const existingFormState = prev[tabKey]
        if (
          existingFormState &&
          (editModes[tabKey] ||
            localDrafts[tabKey] ||
            existingFormState.phases.length > (plan.phases?.length ?? 0))
        ) {
          return
        }
        if (!existingFormState) {
          newFormStates[tabKey] = {
            planName: plan.tp_heading || 'Plan 1',
            insuranceProvider: plan.insurance_plan_id || null,
            selectedInsurance:
              insuranceList.find(
                (p: InsurancePlan) =>
                  String(p.insurance_plan_id) === plan.insurance_plan_id,
              ) || null,
            ...txPlanDiscountFormFromPlan(plan, activeBillingDiscounts),
            phases:
              plan.phases?.map((p) => ({
                ...p,
                scheduled_date:
                  !p.scheduled_date || isNaN(Date.parse(p.scheduled_date))
                    ? ''
                    : new Date(p.scheduled_date).toISOString().split('T')[0],
              })) || [],
            activeAccordionKeys: [],
            procedureModal: null,
          }
          return
        }
        if (existingFormState.planName !== plan.tp_heading) {
          newFormStates[tabKey] = {
            ...existingFormState,
            planName: plan.tp_heading || 'Plan 1',
          }
        }
        const currentPhaseIds = existingFormState.phases
          .map((p) => p.phase_id)
          .sort()
        const reduxPhaseIds = plan.phases?.map((p) => p.phase_id).sort() || []
        const phasesChanged =
          JSON.stringify(currentPhaseIds) !== JSON.stringify(reduxPhaseIds) ||
          existingFormState.phases.some(
            (localPhase, idx) =>
              localPhase.status !== plan.phases?.[idx]?.status,
          ) ||
          existingFormState.phases.length !== (plan.phases?.length || 0)
        if (phasesChanged) {
          const syncedPhases =
            plan.phases?.map((p) => ({
              ...p,
              scheduled_date:
                !p.scheduled_date || isNaN(Date.parse(p.scheduled_date))
                  ? ''
                  : new Date(p.scheduled_date).toISOString().split('T')[0],
            })) || []
          newFormStates[tabKey] = {
            ...existingFormState,
            phases: syncedPhases,
            activeAccordionKeys: remapTxPlanAccordionKeys(
              existingFormState.phases,
              syncedPhases,
              existingFormState.activeAccordionKeys,
            ),
          }
        }
      })
      if (Object.keys(newFormStates).length > 0) {
        return { ...prev, ...newFormStates }
      }
      return prev
    })
  }, [
    treatmentPlans,
    activeBillingDiscounts,
    insuranceList,
    selectedPatient,
    editModes,
    localDrafts,
  ])

  const patientInsurancePlans = (
    selectedPatient as unknown as PatientWithInsurance | null
  )?.insurance_plans

  const clearTreatmentPlanDropIndicators = useCallback((): void => {
    if (phaseDragOverRafRef.current !== null) {
      window.cancelAnimationFrame(phaseDragOverRafRef.current)
      phaseDragOverRafRef.current = null
    }
    phaseDragOverLatestRef.current = null
    phaseDragOverCommittedRef.current = { phase: null, insert: null }
    setActiveDropPhaseOriginalIdx(null)
    setDropInsertBeforeVisibleIdx(null)
  }, [])

  const clearTreatmentPlanDragUi = useCallback((): void => {
    clearTreatmentPlanDropIndicators()
    setDraggingItemId(null)
  }, [clearTreatmentPlanDropIndicators])

  useEffect(() => {
    return () => {
      if (phaseDragOverRafRef.current !== null) {
        window.cancelAnimationFrame(phaseDragOverRafRef.current)
      }
    }
  }, [])

  const handleTreatmentPlanPhaseProcedureDrop = useCallback(
    (tabKey: string, toOriginalIdx: number) =>
      (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault()
        e.stopPropagation()
        const payload = parseTxPlanItemDragPayload(
          e.dataTransfer.getData(TX_PLAN_ITEM_DRAG_MIME),
        )
        if (!payload) return

        const formSnapshot = formStatesRef.current[tabKey]
        if (!formSnapshot) return

        const phases = formSnapshot.phases
        const fromOriginalIdx = payload.fromPhaseOriginalIdx
        if (
          fromOriginalIdx < 0 ||
          fromOriginalIdx >= phases.length ||
          toOriginalIdx < 0 ||
          toOriginalIdx >= phases.length
        ) {
          return
        }

        const rawInsertBefore = getInsertBeforeVisibleIndexFromPointer(
          e.currentTarget,
          e.clientY,
        )

        let insertBeforeVisible = rawInsertBefore

        if (fromOriginalIdx === toOriginalIdx) {
          const fromVisible = getVisibleItemIndex(
            phases[fromOriginalIdx].items,
            payload.itemId,
          )
          if (fromVisible < 0) return
          insertBeforeVisible = adjustInsertBeforeVisibleForSamePhaseReorder(
            fromVisible,
            rawInsertBefore,
          )
          if (insertBeforeVisible === fromVisible) {
            return
          }
        }

        queueHistorySnapshotBefore(tabKey)

        setFormStates((prev) => {
          const prevState = prev[tabKey]
          if (!prevState) return prev
          const nextPhases = movePhaseItem(
            prevState.phases,
            fromOriginalIdx,
            toOriginalIdx,
            payload.itemId,
            insertBeforeVisible,
          )
          return {
            ...prev,
            [tabKey]: {
              ...prevState,
              phases: nextPhases,
            },
          }
        })

        clearTreatmentPlanDragUi()
        markHistorySavePending(tabKey)
      },
    [
      clearTreatmentPlanDragUi,
      markHistorySavePending,
      queueHistorySnapshotBefore,
    ],
  )

  const handleTreatmentPlanPhaseProcedureDragOver = useCallback(
    (originalIdx: number) =>
      (ev: React.DragEvent<HTMLDivElement>): void => {
        // Capture phase + preventDefault: required so drops work when the pointer is
        // over nested Syncfusion grid cells (grid may stop propagation on bubble).
        ev.preventDefault()
        ev.dataTransfer.dropEffect = 'move'
        phaseDragOverLatestRef.current = {
          originalIdx,
          clientY: ev.clientY,
          el: ev.currentTarget,
        }
        if (phaseDragOverRafRef.current !== null) {
          return
        }
        phaseDragOverRafRef.current = window.requestAnimationFrame(() => {
          phaseDragOverRafRef.current = null
          const pending = phaseDragOverLatestRef.current
          if (!pending) {
            return
          }
          const insert = getInsertBeforeVisibleIndexFromPointer(
            pending.el,
            pending.clientY,
          )
          const phase = pending.originalIdx
          const prevCommitted = phaseDragOverCommittedRef.current
          if (
            prevCommitted.phase !== phase ||
            prevCommitted.insert !== insert
          ) {
            phaseDragOverCommittedRef.current = { phase, insert }
            setActiveDropPhaseOriginalIdx(phase)
            setDropInsertBeforeVisibleIdx(insert)
          }
        })
      },
    [],
  )

  const saveTreatmentPlanAsync = useCallback(
    async (tabKey: string, isDraft: boolean): Promise<boolean> => {
      const formState = formStatesRef.current[tabKey]
      if (!formState || !selectedPatient) {
        showToast(
          createFailedToast(
            'Cannot save treatment plan',
            !selectedPatient
              ? 'Please select a patient before saving the treatment plan.'
              : 'Treatment plan form is still loading. Try again in a moment.',
          ),
        )
        return false
      }
      if (!selectedClinic?.clinic_id) {
        showToast(
          createFailedToast(
            'Cannot save treatment plan',
            'Please select a clinic before saving the treatment plan.',
          ),
        )
        return false
      }
      if (!isDraft) {
        if (!formState.planName.trim()) {
          showToast(
            createFailedToast(
              'Cannot save treatment plan',
              'Please enter a treatment plan name before saving.',
            ),
          )
          return false
        }
        if (!validateProvidersAndPhasesBeforeSave(tabKey, formState.phases)) {
          return false
        }
      }
      const phasesWithDates = formState.phases.map((p) => ({
        ...p,
        scheduled_date: p.scheduled_date
          ? new Date(p.scheduled_date).toISOString().split('T')[0]
          : null,
      }))
      const existingPlan = treatmentPlansRef.current.find(
        (p) => p.tp_id === tabKey,
      )
      const isLocalDraft = localDraftsRef.current[tabKey]
      const discountPayload = txPlanDiscountPayloadFromForm(
        formState.selectedDiscountId,
        formState.customDiscount,
      )
      const payload: ITreatmentPlan = {
        tp_id: existingPlan?.tp_id || '',
        patient_id: existingPlan?.patient_id || selectedPatient.patient_id,
        tp_heading: formState.planName,
        clinic_id: selectedClinic.clinic_id,
        insurance_plan_id: formState.insuranceProvider,
        phases: phasesWithDates,
        discount_id: discountPayload.discount_id,
        discount: discountPayload.discount,
        isDraft: isDraft,
      }
      if (existingPlan) {
        clearHistory(tabKey)
        const previousPhases = formState.phases
        try {
          const savedPlan = await dispatch(
            updateTreatmentPlan({
              treatmentPlanId: payload.tp_id,
              treatmentPlan: payload,
            }),
          ).unwrap()
          const mappedPhases =
            savedPlan.phases?.map((p) => ({
              ...p,
              scheduled_date:
                !p.scheduled_date || isNaN(Date.parse(p.scheduled_date))
                  ? ''
                  : new Date(p.scheduled_date).toISOString().split('T')[0],
            })) || []
          setFormStates((prev) => {
            const current = prev[tabKey]
            if (!current) return prev
            return {
              ...prev,
              [tabKey]: {
                ...current,
                phases: mappedPhases,
                activeAccordionKeys: remapTxPlanAccordionKeys(
                  previousPhases,
                  mappedPhases,
                  current.activeAccordionKeys,
                ),
              },
            }
          })
          clearHistory(tabKey)
          setEditModes((prev) => ({ ...prev, [tabKey]: false }))
          showToast(createTreatmentPlanUpdatedToast())
          return true
        } catch (error) {
          showToast(
            createFailedToUpdateTreatmentPlanToast(
              error instanceof Error ? error.message : undefined,
            ),
          )
          return false
        }
      }
      if (isLocalDraft) {
        const draftFormState = formStatesRef.current[tabKey]
        try {
          const result = await dispatch(
            addTreatmentPlan({ treatmentPlan: payload }),
          ).unwrap()
          if (result && typeof result === 'object' && 'tp_id' in result) {
            const savedPlan = result as ITreatmentPlan
            const newPlanId = savedPlan.tp_id
            pendingCreatedPlanIdRef.current = newPlanId
            const mappedPhases =
              savedPlan.phases?.map((p) => ({
                ...p,
                scheduled_date:
                  !p.scheduled_date || isNaN(Date.parse(p.scheduled_date))
                    ? ''
                    : new Date(p.scheduled_date).toISOString().split('T')[0],
              })) || []
            setLocalDrafts((prev) => {
              const newDrafts = { ...prev }
              delete newDrafts[tabKey]
              return newDrafts
            })
            setFormStates((prev) => {
              const next = { ...prev }
              delete next[tabKey]
              if (draftFormState) {
                next[newPlanId] = {
                  ...draftFormState,
                  phases: mappedPhases,
                  activeAccordionKeys: remapTxPlanAccordionKeys(
                    draftFormState.phases,
                    mappedPhases,
                    draftFormState.activeAccordionKeys,
                  ),
                }
              }
              return next
            })
            setEditModes((prev) => {
              const newModes = { ...prev }
              delete newModes[tabKey]
              return newModes
            })
            clearHistory(tabKey)
            setActiveTabKey(newPlanId)
            showToast(createTreatmentPlanCreatedToast())
          }
          return true
        } catch (error) {
          pendingCreatedPlanIdRef.current = null
          showToast(
            createFailedToCreateTreatmentPlanToast(
              error instanceof Error ? error.message : undefined,
            ),
          )
          return false
        }
      }
      return false
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads formStates/treatmentPlans/localDrafts via refs
    [
      validateProvidersAndPhasesBeforeSave,
      selectedPatient,
      selectedClinic?.clinic_id,
      dispatch,
      showToast,
    ],
  )

  const handleSave = (tabKey: string, isDraft: boolean): void => {
    void saveTreatmentPlanAsync(tabKey, isDraft)
  }

  const handleCancel = useCallback(
    (tabKey: string): void => {
      if (editingTabName === tabKey) {
        setEditingTabName(null)
        setEditingTabValue('')
      }
      clearProviderValidationForTab(tabKey)
      const isLocalDraft = localDrafts[tabKey]
      const existingPlan = treatmentPlans.find((p) => p.tp_id === tabKey)

      if (isLocalDraft) {
        // Remove unsaved plan completely
        setLocalDrafts((prev) => {
          const newDrafts = { ...prev }
          delete newDrafts[tabKey]
          return newDrafts
        })
        setFormStates((prev) => {
          const newStates = { ...prev }
          delete newStates[tabKey]
          return newStates
        })
        setEditModes((prev) => {
          const newModes = { ...prev }
          delete newModes[tabKey]
          return newModes
        })
        // Switch to first available tab
        if (activeTreatmentPlans.length > 0) {
          setActiveTabKey(activeTreatmentPlans[0].tp_id)
        } else {
          setActiveTabKey('')
        }
      } else if (existingPlan) {
        // Reset form to last saved state and exit edit mode
        const existingFormState = formStates[tabKey]
        setFormStates((prev) => {
          return {
            ...prev,
            [tabKey]: {
              planName: existingPlan.tp_heading || 'Plan 1',
              insuranceProvider: existingPlan.insurance_plan_id || null,
              selectedInsurance:
                insuranceList.find(
                  (p: InsurancePlan) =>
                    String(p.insurance_plan_id) ===
                    existingPlan.insurance_plan_id,
                ) || null,
              ...txPlanDiscountFormFromPlan(
                existingPlan,
                activeBillingDiscounts,
              ),
              phases:
                existingPlan.phases?.map((p) => ({
                  ...p,
                  scheduled_date:
                    !p.scheduled_date || isNaN(Date.parse(p.scheduled_date))
                      ? ''
                      : new Date(p.scheduled_date).toISOString().split('T')[0],
                })) || [],
              activeAccordionKeys: existingFormState?.activeAccordionKeys || [],
              procedureModal: existingFormState?.procedureModal || null,
            },
          }
        })
        // Exit edit mode
        setEditModes((prev) => ({ ...prev, [tabKey]: false }))
      }
    },
    [
      editingTabName,
      clearProviderValidationForTab,
      localDrafts,
      treatmentPlans,
      activeTreatmentPlans,
      formStates,
      insuranceList,
      activeBillingDiscounts,
    ],
  )

  const handleResetChart = (tabKey: string): void => {
    // Reuse handleCancel logic to reset to last saved state
    handleCancel(tabKey)
    // Clear history after reset
    clearHistory(tabKey)
  }

  const confirmStatusChange = (): void => {
    if (!pendingStatusChange) return
    dispatch(
      updateTreatmentPlan({
        treatmentPlanId: pendingStatusChange.treatmentPlanId,
        treatmentPlan: {
          phases: pendingStatusChange.treatmentPlan.phases
            ?.map((p) => ({
              ...p,
              status: pendingStatusChange.newStatus,
            }))
            .filter((p) => {
              const pendingId = pendingStatusChange.phaseId
              if (pendingId != null && pendingId !== '') {
                return (p.phase_id ?? '') === pendingId
              }
              return p.phase_name === pendingStatusChange.phaseName
            }),
        },
      }),
    )
    setPendingStatusChange(null)
  }

  const confirmDeleteTreatmentPlan = (): void => {
    if (!pendingDelete) return
    dispatch(deleteTreatmentPlan(pendingDelete.treatmentPlanId))
      .unwrap()
      .then(() => {
        showToast(
          createTreatmentPlanDeletedToast(pendingDelete.treatmentPlanName),
        )
      })
      .catch((error) => {
        showToast(
          createFailedToDeleteTreatmentPlanToast(
            error instanceof Error ? error.message : undefined,
          ),
        )
      })
    setPendingDelete(null)
    // Switch to create-new tab if deleted plan was active
    if (activeTabKey === pendingDelete.treatmentPlanId) {
      setActiveTabKey('create-new')
    }
  }

  const handleDeleteTreatmentPlan = (id: string, planName: string): void => {
    setPendingDelete({
      treatmentPlanId: id,
      treatmentPlanName: planName,
    })
  }

  const enterEditMode = (tabKey: string): void => {
    if (localDrafts[tabKey] || editModes[tabKey]) {
      return
    }
    setEditModes((prev) => ({ ...prev, [tabKey]: true }))
    setActiveTabKey(tabKey)
    setTimeout(() => {
      saveHistorySnapshot(tabKey)
    }, 0)
  }

  const toggleEditMode = (tabKey: string): void => {
    const wasEditMode = editModes[tabKey] || false
    if (wasEditMode && editingTabName === tabKey) {
      setEditingTabName(null)
      setEditingTabValue('')
    }
    setEditModes((prev) => ({ ...prev, [tabKey]: !prev[tabKey] }))
    setActiveTabKey(tabKey)
    if (!wasEditMode) {
      setTimeout(() => {
        saveHistorySnapshot(tabKey)
      }, 0)
    }
  }
  toggleEditModeRef.current = toggleEditMode

  const handleDiscardPlanEdits = (tabKey: string): void => {
    handleCancel(tabKey)
    clearHistory(tabKey)
  }

  const handleTabSelect = useCallback(
    (key: string | null): void => {
      if (!key || key === activeTabKey) return
      if (editingTabName !== null) {
        setEditingTabName(null)
        setEditingTabValue('')
      }
      setActiveTabKey(key)
    },
    [activeTabKey, editingTabName],
  )

  // Convert vertical mouse wheel scrolling to horizontal scrolling
  const handleTabsWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>): void => {
      const element = tabsScrollableRef.current
      if (!element) return

      // Only convert vertical scrolling to horizontal if there's horizontal overflow
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault()
        element.scrollLeft += e.deltaY
      }
    },
    [],
  )

  const handleDuplicatePlan = (sourcePlanId: string): void => {
    const sourcePlan = treatmentPlans.find((p) => p.tp_id === sourcePlanId)
    if (!sourcePlan || !selectedPatient) return

    // Deep clone the plan data
    const duplicatedPhases: IPhases[] =
      sourcePlan.phases?.map((phase) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { phase_id: _phaseId, ...phaseWithoutId } = phase
        return {
          ...phaseWithoutId,
          status: TX_PLAN_STATUS.PROPOSED,
          items:
            phase.items?.map((item): IPhaseItems => {
              return {
                ...item,
                item_id: '', // Will be assigned new ID when saved
              }
            }) || [],
        }
      }) || []

    // Generate a unique draft ID
    const newDraftId = `draft-${Date.now()}`

    // Determine the duplicate number for the plan name
    const basePlanName = sourcePlan.tp_heading || 'Untitled Plan'
    const allPlanNames = [
      ...treatmentPlans.map((p) => p.tp_heading || 'Untitled Plan'),
      ...Object.keys(formStates).map(
        (key) => formStates[key]?.planName || 'Untitled Plan',
      ),
    ]

    const duplicatedPlanName = generateDuplicatePlanName(
      basePlanName,
      allPlanNames,
    )

    // Create form state for the duplicate
    const duplicatedFormState = {
      planName: duplicatedPlanName,
      insuranceProvider: sourcePlan.insurance_plan_id || null,
      selectedInsurance:
        insuranceList.find(
          (ins) => ins.insurance_plan_id === sourcePlan.insurance_plan_id,
        ) || null,
      ...txPlanDiscountFormFromPlan(sourcePlan, billingDiscounts),
      phases: duplicatedPhases,
      activeAccordionKeys: duplicatedPhases.map((_, idx) => `phase-${idx}`),
      procedureModal: null,
    }

    // Create the local draft
    setLocalDrafts((prev) => ({
      ...prev,
      [newDraftId]: true,
    }))

    // Set form state
    setFormStates((prev) => ({
      ...prev,
      [newDraftId]: duplicatedFormState,
    }))

    // Set edit mode
    setEditModes((prev) => ({
      ...prev,
      [newDraftId]: true,
    }))

    // Switch to the new draft tab
    setActiveTabKey(newDraftId)
  }

  const handlePlanMenuSelect = (value: string, tabKey: string): void => {
    switch (value) {
      case 'delete-plan':
        const plan = treatmentPlans.find((p) => p.tp_id === tabKey)
        if (plan) {
          handleDeleteTreatmentPlan(
            plan.tp_id,
            plan.tp_heading || 'Untitled Plan',
          )
        } else if (localDrafts[tabKey]) {
          // Delete local draft
          setLocalDrafts((prev) => {
            const newDrafts = { ...prev }
            delete newDrafts[tabKey]
            return newDrafts
          })
          setFormStates((prev) => {
            const newStates = { ...prev }
            delete newStates[tabKey]
            return newStates
          })
          setEditModes((prev) => {
            const newModes = { ...prev }
            delete newModes[tabKey]
            return newModes
          })
          // Switch to first available tab
          if (treatmentPlans.length > 0) {
            setActiveTabKey(treatmentPlans[0].tp_id)
          } else {
            setActiveTabKey('')
          }
        }
        break
    }
  }

  const updateFormState = <K extends keyof (typeof formStates)[string]>(
    tabKey: string,
    field: K,
    value: (typeof formStates)[string][K],
    shouldSaveHistory: boolean = false,
  ): void => {
    if (shouldSaveHistory) {
      queueHistorySnapshotBefore(tabKey)
    }

    setFormStates((prev) => {
      const formState = prev[tabKey]
      if (!formState) return prev
      const updatedState = {
        ...formState,
        [field]: value,
      }
      // Recalculate insurance if insurance or discount changed
      if (
        field === 'selectedInsurance' ||
        field === 'selectedDiscountId' ||
        field === 'customDiscount'
      ) {
        updatedState.phases = recalculatePhaseItems(
          updatedState.phases,
          updatedState.selectedInsurance,
          updatedState.selectedDiscountId,
          updatedState.customDiscount,
          billingDiscounts,
          insuranceList,
          patientInsurancePlans,
          allowManualFeeOverride,
        )
      }

      return {
        ...prev,
        [tabKey]: updatedState,
      }
    })
    if (shouldSaveHistory) {
      markHistorySavePending(tabKey)
    }
  }

  const handleProcedureFeeChange = useCallback(
    ({
      tabKey,
      phaseOriginalIdx,
      item,
      clinicFee,
    }: TxPlanProcedureFeeChangeArgs): void => {
      if (!allowManualFeeOverride) return
      queueHistorySnapshotBefore(tabKey)
      setFormStates((prev) => {
        const formState = prev[tabKey]
        if (!formState) return prev
        return {
          ...prev,
          [tabKey]: {
            ...formState,
            phases: formState.phases.map((phase, idx) => {
              if (idx !== phaseOriginalIdx) return phase
              return {
                ...phase,
                items: phase.items.map((phaseItem) => {
                  if (phaseItem.item_id !== item.item_id) return phaseItem
                  return applyClinicFeeToPhaseItem(phaseItem, clinicFee, {
                    selectedInsurance: formState.selectedInsurance,
                    selectedDiscountId: formState.selectedDiscountId,
                    customDiscount: formState.customDiscount,
                    discountsForPercent: activeBillingDiscounts,
                    insuranceList,
                    patientInsurancePlans,
                  })
                }),
              }
            }),
          },
        }
      })
      markHistorySavePending(tabKey)
    },
    [
      activeBillingDiscounts,
      allowManualFeeOverride,
      insuranceList,
      patientInsurancePlans,
      queueHistorySnapshotBefore,
      markHistorySavePending,
    ],
  )

  const handleInsuranceSelect = (carrier: string, tabKey: string): void => {
    // For existing plans in view mode, enable edit mode when insurance changes
    // This triggers edit mode, same as clicking "Edit Plan"
    const isLocalDraft = localDrafts[tabKey] || false
    const isEditMode = isLocalDraft || editModes[tabKey] || false
    const wasInEditMode = isEditMode

    const selectedInsurance =
      insuranceList.find(
        (plan: InsurancePlan) => plan.carrier_name === carrier,
      ) || null

    if (selectedInsurance) {
      // If we just entered edit mode, wait for state to update, then initialize history and make change
      if (!wasInEditMode && !isLocalDraft) {
        // Set edit mode first
        setEditModes((prev) => ({ ...prev, [tabKey]: true }))
        // Mark that we need to initialize history when edit mode is set
        markHistoryInitPending(tabKey)
        // Make the change - the useEffect will initialize history first
        // Use requestAnimationFrame to ensure state updates are processed
        requestAnimationFrame(() => {
          updateFormState(
            tabKey,
            'insuranceProvider',
            selectedInsurance.insurance_plan_id || '',
            false, // Don't save history again
          )
          updateFormState(tabKey, 'selectedInsurance', selectedInsurance, false)
          // Mark that we need to save history after state update for redo (this is the "after" state)
          markHistorySavePending(tabKey)
        })
      } else {
        queueHistorySnapshotBefore(tabKey)
        updateFormState(
          tabKey,
          'insuranceProvider',
          selectedInsurance.insurance_plan_id || '',
          false, // Don't save history again
        )
        updateFormState(tabKey, 'selectedInsurance', selectedInsurance, false)
        // Mark that we need to save history after state update for redo (this is the "after" state)
        markHistorySavePending(tabKey)
      }
    }
  }

  const handleDiscountSelect = (discountId: string, tabKey: string): void => {
    // For existing plans in view mode, enable edit mode when discount changes
    const isLocalDraft = localDrafts[tabKey] || false
    const isEditMode = isLocalDraft || editModes[tabKey] || false
    if (!isLocalDraft && !isEditMode) {
      setEditModes((prev) => ({ ...prev, [tabKey]: true }))
    }

    updateFormState(tabKey, 'selectedDiscountId', discountId, true) // Save history
  }

  const handleCustomDiscountChange = (value: number, tabKey: string): void => {
    // For existing plans in view mode, enable edit mode when discount changes
    const isLocalDraft = localDrafts[tabKey] || false
    const isEditMode = isLocalDraft || editModes[tabKey] || false
    if (!isLocalDraft && !isEditMode) {
      setEditModes((prev) => ({ ...prev, [tabKey]: true }))
    }

    updateFormState(
      tabKey,
      'selectedDiscountId',
      TX_PLAN_DISCOUNT_CUSTOM_ID,
      true,
    ) // Save history
    updateFormState(tabKey, 'customDiscount', value, false)
  }

  // Open tooth modal for editing
  const editToothSelection = (
    tabKey: string,
    phaseIdx: number,
    item: IPhaseItems,
  ): void => {
    const phaseItems = formStates[tabKey]?.phases[phaseIdx]?.items ?? []
    const itemIndex = phaseItems.findIndex(
      (phaseItem) => phaseItem.item_id && phaseItem.item_id === item.item_id,
    )
    const siblings =
      itemIndex >= 0
        ? collectPhaseToothSurfaceSiblings(phaseItems, itemIndex)
        : [item]
    const siblingItemIds = siblings
      .map((sibling) => sibling.item_id)
      .filter((id): id is string => Boolean(id))
    setSelectedItemForToothEdit({
      tabKey,
      phaseIdx,
      item: siblings[0] ?? item,
      siblingItemIds,
    })
    setIsToothModalOpen(true)
  }

  const initialToothSelectionsForEdit = useMemo((): IToothDetails[] => {
    if (!selectedItemForToothEdit) {
      return []
    }
    const { tabKey, phaseIdx, item, siblingItemIds } = selectedItemForToothEdit
    const phaseItems = formStates[tabKey]?.phases[phaseIdx]?.items ?? []
    const siblingIdSet = new Set(siblingItemIds)
    const itemsToMerge =
      siblingItemIds.length > 0
        ? phaseItems.filter(
            (phaseItem) =>
              phaseItem.item_id && siblingIdSet.has(phaseItem.item_id),
          )
        : [item]
    const entries = mergePhaseItemsToToothSurfaceEntries(itemsToMerge)
    return groupToothSurfaceEntriesAsToothDetails(entries)
  }, [formStates, selectedItemForToothEdit])

  const toothEditProcedureSurfaceSelection = useMemo(() => {
    if (!selectedItemForToothEdit) {
      return null
    }
    const procedure =
      selectedItemForToothEdit.item.procedure_settings?.procedure
    return resolveProcedureSurfaceSelectionRules(
      procedure?.procedure_dental_conditions ??
        selectedItemForToothEdit.item.proceduredentalcondition,
      procedure?.description ??
        selectedItemForToothEdit.item.procedure_settings?.description ??
        null,
    )
  }, [selectedItemForToothEdit])

  const saveToothSelection = (toothData: IToothDetails[]): void => {
    if (!selectedItemForToothEdit) return

    const { tabKey, phaseIdx, item, siblingItemIds } = selectedItemForToothEdit
    const siblingIdSet = new Set(siblingItemIds)
    const primaryItemId =
      item.item_id ??
      `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const surfaceString = formatToothDetailsToString(toothData)
    const toothSurfaces = convertToothDetailsToToothSurfaces(
      toothData,
      primaryItemId,
    )

    queueHistorySnapshotBefore(tabKey)
    setFormStates((prev) => {
      const prevState = prev[tabKey]
      if (!prevState) return prev
      return {
        ...prev,
        [tabKey]: {
          ...prevState,
          phases: prevState.phases.map((phase, idx) => {
            if (idx !== phaseIdx) return phase
            let inserted = false
            const nextItems: IPhaseItems[] = []
            phase.items.forEach((phaseItem) => {
              const isSibling =
                phaseItem.item_id && siblingIdSet.has(phaseItem.item_id)
              if (!isSibling) {
                nextItems.push(phaseItem)
                return
              }
              if (!inserted) {
                nextItems.push({
                  ...phaseItem,
                  item_id: primaryItemId,
                  surface: surfaceString || null,
                  tooth_surfaces: toothSurfaces,
                })
                inserted = true
              }
            })
            if (!inserted) {
              nextItems.push({
                ...item,
                item_id: primaryItemId,
                surface: surfaceString || null,
                tooth_surfaces: toothSurfaces,
              })
            }
            return { ...phase, items: nextItems }
          }),
        },
      }
    })

    markHistorySavePending(tabKey)
    setIsToothModalOpen(false)
    setSelectedItemForToothEdit(null)
  }

  // Close tooth modal
  const closeToothModal = (): void => {
    setIsToothModalOpen(false)
    setSelectedItemForToothEdit(null)
  }

  // Skip tooth selection
  const skipToothSelection = (): void => {
    closeToothModal()
  }

  const hasFormChanges = (tabKey: string): boolean => {
    const formState = formStates[tabKey]
    if (!formState) {
      return false
    }
    return hasTxPlanFormChanges({
      formState,
      existingPlan: treatmentPlans.find((p) => p.tp_id === tabKey),
      isLocalDraft: Boolean(localDrafts[tabKey]),
      isEditMode: Boolean(editModes[tabKey]),
      billingDiscounts,
    })
  }

  const requestDiscardPlanEdits = (tabKey: string): void => {
    const isLocalDraft = Boolean(localDrafts[tabKey])
    if (!isLocalDraft && !hasFormChanges(tabKey)) {
      handleDiscardPlanEdits(tabKey)
      return
    }
    const existingPlan = treatmentPlans.find((p) => p.tp_id === tabKey)
    const planName = isLocalDraft
      ? formStates[tabKey]?.planName || 'Draft'
      : existingPlan?.tp_heading || 'Untitled Plan'
    setPendingDiscard({ tabKey, isLocalDraft, planName })
  }

  const confirmDiscardPlanEdits = (): void => {
    if (!pendingDiscard) return
    handleDiscardPlanEdits(pendingDiscard.tabKey)
    setPendingDiscard(null)
  }

  const getUnsavedTabKeys = useCallback((): string[] => {
    const keys = new Set<string>([
      ...Object.keys(localDrafts),
      ...Object.keys(formStates),
    ])
    return Array.from(keys).filter(
      (tabKey) => Boolean(localDrafts[tabKey]) || hasFormChanges(tabKey),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasFormChanges mirrors form/edit state above
  }, [
    localDrafts,
    formStates,
    editModes,
    treatmentPlans,
    activeBillingDiscounts,
  ])

  useEffect(() => {
    if (!navigationRef) {
      return
    }
    navigationRef.current = {
      hasUnsavedChanges: () => getUnsavedTabKeys().length > 0,
      discardUnsavedChanges: () => {
        getUnsavedTabKeys().forEach((tabKey) => {
          handleCancel(tabKey)
        })
      },
      saveUnsavedChanges: async () => {
        const dirtyTabKeys = getUnsavedTabKeys()
        for (const tabKey of dirtyTabKeys) {
          const saved = await saveTreatmentPlanAsync(tabKey, false)
          if (!saved) {
            return false
          }
        }
        return true
      },
    }
    return () => {
      navigationRef.current = null
    }
  }, [navigationRef, getUnsavedTabKeys, saveTreatmentPlanAsync, handleCancel])

  const renderTreatmentPlanForm = (
    tabKey: string,
    plan: ITreatmentPlan | null,
  ): JSX.Element => {
    const formState = formStates[tabKey]
    if (!formState) return <div>Loading...</div>

    const isLocalDraft = localDrafts[tabKey] || false
    const isEditMode = isLocalDraft || editModes[tabKey] || false
    const phaseValidationErrorKeys =
      phaseValidationErrorKeysByTab[tabKey] ?? EMPTY_PHASE_VALIDATION_KEY_SET

    const totals: {
      charges: number
      insurance: number
      adjustments: number
      outstanding: number
    } = formState.phases.reduce(
      (sum, phase) => {
        for (const item of phase.items) {
          sum.charges += item.charges
          sum.insurance += item.insurance
          sum.adjustments += item.adjustments
          sum.outstanding += item.outstanding
        }
        return sum
      },
      { charges: 0, insurance: 0, adjustments: 0, outstanding: 0 },
    )
    const visiblePhaseCount = formState.phases.filter(
      (p) => !p.is_hidden,
    ).length

    return (
      <div className="treatment-plan-form-container">
        {/* Plan Header */}
        <div className="create-tx-plan-container">
          <Row className="align-items-center gx-0">
            <Col className="d-flex align-items-end flex-nowrap gap-1 create-tx-plan-phase-header">
              {/* Plan name removed from form - now only editable via tab */}
            </Col>

            <Col
              md="12"
              className="d-flex justify-content-end align-items-center ctx-custom-buttons"
            ></Col>
          </Row>

          {/* Phases */}
          <Accordion
            id={`tx-plan-phase-accordion-${tabKey}`}
            className="tx-plan-phase-accordion"
            defaultActiveKey={[]}
            activeKey={formState.activeAccordionKeys}
            alwaysOpen
          >
            {formState.phases
              .map((phase, originalIdx) => ({ phase, originalIdx }))
              .filter(({ phase }) => !phase.is_hidden)
              .map(({ phase, originalIdx }, phaseIdx) => {
                const canMoveUp = phaseIdx > 0
                const canMoveDown = phaseIdx < visiblePhaseCount - 1
                // Check if this is the first phase in the first treatment plan tab
                const isFirstTab =
                  activeTreatmentPlans.length > 0
                    ? tabKey === activeTreatmentPlans[0]?.tp_id
                    : Object.keys(localDrafts).length > 0
                      ? tabKey === Object.keys(localDrafts)[0]
                      : false
                const isFirstPhase = phaseIdx === 0
                const isFirstPhaseInFirstTab = isFirstTab && isFirstPhase
                const phaseEditKey = getPhaseEditKey(tabKey, phase, originalIdx)
                const isEditingPhaseName = editingPhaseKey === phaseEditKey
                const phaseHeaderKey = txPlanPhaseHeaderKey(phase, originalIdx)
                const showPhaseValidationWarning =
                  phaseValidationErrorKeys.has(phaseHeaderKey)

                return (
                  <Accordion.Item
                    eventKey={txPlanPhaseAccordionEventKey(phase, originalIdx)}
                    key={txPlanPhaseAccordionEventKey(phase, originalIdx)}
                    className="rounded"
                  >
                    <div
                      className={`accordion-header tx-plan-phase-header border-0${
                        isFirstPhaseInFirstTab
                          ? ' first-phase-header-no-radius'
                          : ''
                      }`}
                    >
                      <AccordionButton
                        as="div"
                        className="accordion-button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setFormStates((prev) => {
                            const prevState = prev[tabKey]
                            if (!prevState) return prev
                            const phaseId = txPlanPhaseAccordionEventKey(
                              phase,
                              originalIdx,
                            )
                            const isActive =
                              prevState.activeAccordionKeys.includes(phaseId)
                            return {
                              ...prev,
                              [tabKey]: {
                                ...prevState,
                                activeAccordionKeys: isActive
                                  ? prevState.activeAccordionKeys.filter(
                                      (k) => k !== phaseId,
                                    )
                                  : [...prevState.activeAccordionKeys, phaseId],
                              },
                            }
                          })
                        }}
                      >
                        <div className="w-100">
                          <Row className="align-items-center">
                            <Col className="d-flex flex-row align-items-center gap-2 flex-wrap">
                              <div className="create-tx-plan-set-date d-flex flex-row align-items-center gap-2 flex-wrap">
                                <div className="create-tx-plan-phase-name d-flex align-items-center gap-1">
                                  {isEditMode && isEditingPhaseName ? (
                                    <Form.Control
                                      type="text"
                                      className="create-tx-plan-phase-name-input"
                                      size="sm"
                                      aria-label="Phase name"
                                      value={editingPhaseValue}
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => {
                                        e.stopPropagation()
                                        if (e.key === 'Enter') {
                                          handleSavePhaseName(
                                            tabKey,
                                            originalIdx,
                                            phaseIdx,
                                          )
                                        } else if (e.key === 'Escape') {
                                          handleCancelEditPhaseName()
                                        }
                                      }}
                                      onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                      ) => {
                                        setEditingPhaseValue(e.target.value)
                                      }}
                                      onBlur={() =>
                                        handleSavePhaseName(
                                          tabKey,
                                          originalIdx,
                                          phaseIdx,
                                        )
                                      }
                                      autoFocus
                                    />
                                  ) : (
                                    <>
                                      <span className="create-tx-plan-phase-name-label">
                                        {phase.phase_name}
                                      </span>
                                      {isEditMode && (
                                        <i
                                          className="mgc_pencil_fill create-tx-plan-phase-name-edit"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            handleStartEditPhaseName(
                                              tabKey,
                                              phase,
                                              originalIdx,
                                              phase.phase_name,
                                            )
                                          }}
                                          title="Rename phase"
                                        />
                                      )}
                                    </>
                                  )}
                                </div>
                                {isEditMode && (
                                  <div
                                    onClick={(e) => e.stopPropagation()}
                                    className="d-flex flex-row align-items-center gap-2 flex-wrap"
                                  >
                                    <DatePicker
                                      className="create-tx-plan-date-picker"
                                      portalId="root"
                                      popperClassName="create-tx-plan-phase-datepicker-popper"
                                      popperProps={{ strategy: 'fixed' }}
                                      selected={
                                        !phase.scheduled_date ||
                                        isNaN(Date.parse(phase.scheduled_date))
                                          ? null
                                          : new Date(phase.scheduled_date)
                                      }
                                      onChange={(date: Date | null) => {
                                        queueHistorySnapshotBefore(tabKey)
                                        if (date !== null) {
                                          const formattedDate =
                                            date.toLocaleDateString('en-CA')

                                          if (
                                            phase.scheduled_date ===
                                            formattedDate
                                          ) {
                                            setFormStates((prev) => {
                                              const prevState = prev[tabKey]
                                              if (!prevState) return prev
                                              return {
                                                ...prev,
                                                [tabKey]: {
                                                  ...prevState,
                                                  phases: prevState.phases.map(
                                                    (p, i) =>
                                                      i === phaseIdx
                                                        ? {
                                                            ...p,
                                                            scheduled_date:
                                                              null,
                                                          }
                                                        : p,
                                                  ),
                                                },
                                              }
                                            })
                                            markHistorySavePending(tabKey)
                                            return
                                          }
                                          setFormStates((prev) => {
                                            const prevState = prev[tabKey]
                                            if (!prevState) return prev
                                            return {
                                              ...prev,
                                              [tabKey]: {
                                                ...prevState,
                                                phases: prevState.phases.map(
                                                  (p, i) =>
                                                    i === originalIdx
                                                      ? {
                                                          ...p,
                                                          scheduled_date:
                                                            formattedDate,
                                                        }
                                                      : p,
                                                ),
                                              },
                                            }
                                          })
                                          markHistorySavePending(tabKey)
                                        } else {
                                          setFormStates((prev) => {
                                            const prevState = prev[tabKey]
                                            if (!prevState) return prev
                                            return {
                                              ...prev,
                                              [tabKey]: {
                                                ...prevState,
                                                phases: prevState.phases.map(
                                                  (p, i) =>
                                                    i === originalIdx
                                                      ? {
                                                          ...p,
                                                          scheduled_date: null,
                                                        }
                                                      : p,
                                                ),
                                              },
                                            }
                                          })
                                          markHistorySavePending(tabKey)
                                        }
                                      }}
                                      customInput={
                                        <div
                                          className="d-flex align-items-center gap-1"
                                          style={{ cursor: 'pointer' }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <input
                                            readOnly
                                            placeholder="Select Date"
                                            value={phase.scheduled_date ?? ''}
                                            className="form-control form-control-sm pe-none"
                                            style={{ width: '140px' }}
                                          />
                                          <i
                                            className="mgc_calendar_2_line"
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                              e.preventDefault()
                                              const el = document.activeElement
                                              if (el instanceof HTMLElement)
                                                el.blur()
                                            }}
                                          />
                                        </div>
                                      }
                                      popperPlacement="bottom-start"
                                      dateFormat="yyyy-MM-dd"
                                    />
                                  </div>
                                )}
                                {!isEditMode && phase.scheduled_date && (
                                  <span>
                                    Scheduled{' '}
                                    {new Date(
                                      phase.scheduled_date,
                                    ).toLocaleDateString()}
                                  </span>
                                )}
                                {!isEditMode && !phase.scheduled_date && (
                                  <span>Unscheduled</span>
                                )}
                              </div>
                            </Col>
                            <Col className="d-flex flex-row gap-2 justify-content-end">
                              <div
                                className="d-flex flex-row gap-1 align-items-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {!isEditMode && (
                                  <>
                                    {showPhaseValidationWarning && (
                                      <i
                                        className="mgc mgc_warning_fill tx-plan-phase-validation-warning"
                                        title="This phase has validation errors. Fix them before saving."
                                        aria-label="Phase validation error"
                                      />
                                    )}
                                    <i
                                      className={`phase-visibility-button ti ${
                                        getPhaseVisibility(
                                          phase.phase_id ||
                                            `phase-${originalIdx}`,
                                        )
                                          ? 'mgc_eye_2_fill'
                                          : 'ti-eye-off'
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        togglePhaseVisibility(
                                          phase.phase_id ||
                                            `phase-${originalIdx}`,
                                        )
                                      }}
                                      title={
                                        getPhaseVisibility(
                                          phase.phase_id ||
                                            `phase-${originalIdx}`,
                                        )
                                          ? 'Hide phase conditions in chart'
                                          : 'Show phase conditions in chart'
                                      }
                                    />
                                    <TxPlanStatusDropdown
                                      value={
                                        (phase.status ||
                                          TX_PLAN_STATUS.PROPOSED) as TxPlanStatus
                                      }
                                      onChange={(newStatus) =>
                                        handlePhaseStatusChange(
                                          newStatus,
                                          tabKey,
                                          originalIdx,
                                          phase,
                                          plan,
                                          false,
                                        )
                                      }
                                    />
                                  </>
                                )}
                                {isEditMode && (
                                  <>
                                    {showPhaseValidationWarning && (
                                      <i
                                        className="mgc mgc_warning_fill tx-plan-phase-validation-warning"
                                        title="This phase has validation errors. Fix them before saving."
                                        aria-label="Phase validation error"
                                      />
                                    )}
                                    <i
                                      className={`phase-visibility-button ti ${
                                        getPhaseVisibility(
                                          phase.phase_id ||
                                            `phase-${originalIdx}`,
                                        )
                                          ? 'mgc_eye_2_fill'
                                          : 'ti-eye-off'
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        togglePhaseVisibility(
                                          phase.phase_id ||
                                            `phase-${originalIdx}`,
                                        )
                                      }}
                                      title={
                                        getPhaseVisibility(
                                          phase.phase_id ||
                                            `phase-${originalIdx}`,
                                        )
                                          ? 'Hide phase conditions in chart'
                                          : 'Show phase conditions in chart'
                                      }
                                    />
                                    <TxPlanStatusDropdown
                                      value={
                                        (phase.status ||
                                          TX_PLAN_STATUS.PROPOSED) as TxPlanStatus
                                      }
                                      onChange={(newStatus) =>
                                        handlePhaseStatusChange(
                                          newStatus,
                                          tabKey,
                                          originalIdx,
                                          phase,
                                          plan,
                                          true,
                                        )
                                      }
                                    />
                                    <PhaseActionMenuButton
                                      tabKey={tabKey}
                                      originalIdx={originalIdx}
                                      phase={phase}
                                      plan={plan}
                                      isEditMode={true}
                                      canMoveUp={canMoveUp}
                                      canMoveDown={canMoveDown}
                                      onMenuSelect={handleMenuSelect}
                                    />
                                  </>
                                )}
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </AccordionButton>
                    </div>
                    <Accordion.Body>
                      {(() => {
                        const visibleItems = phase.items.filter(
                          (item) => !item.is_hidden,
                        )
                        const isActiveDropPhase =
                          isEditMode &&
                          draggingItemId !== null &&
                          activeDropPhaseOriginalIdx === originalIdx
                        const dropLineTopPx =
                          dropInsertBeforeVisibleIdx !== null
                            ? 32 + dropInsertBeforeVisibleIdx * 36
                            : null

                        return (
                          <div
                            className={`create-tx-plan-phase-table${
                              isEditMode ? ' tx-plan-phase-drop-target' : ''
                            }${
                              isActiveDropPhase
                                ? ' tx-plan-phase-drop-target-active'
                                : ''
                            }`}
                            data-phase-original-idx={String(originalIdx)}
                            {...(isEditMode
                              ? {
                                  onDragOverCapture:
                                    handleTreatmentPlanPhaseProcedureDragOver(
                                      originalIdx,
                                    ),
                                  onDropCapture:
                                    handleTreatmentPlanPhaseProcedureDrop(
                                      tabKey,
                                      originalIdx,
                                    ),
                                  onDragLeave: (
                                    ev: React.DragEvent<HTMLDivElement>,
                                  ): void => {
                                    if (
                                      !ev.currentTarget.contains(
                                        ev.relatedTarget as Node | null,
                                      )
                                    ) {
                                      clearTreatmentPlanDropIndicators()
                                    }
                                  },
                                }
                              : {})}
                          >
                            {isActiveDropPhase &&
                              visibleItems.length > 0 &&
                              dropLineTopPx !== null && (
                                <div
                                  className="tx-plan-drop-line"
                                  style={{ top: `${dropLineTopPx}px` }}
                                />
                              )}
                            {isActiveDropPhase && visibleItems.length > 0 && (
                              <div className="tx-plan-drop-target-overlay" />
                            )}
                            {isActiveDropPhase && visibleItems.length === 0 && (
                              <div
                                className="tx-plan-empty-phase-drop-hint"
                                role="status"
                              >
                                Drop procedure here
                              </div>
                            )}
                            {visibleItems.length > 0 && (
                              <TxPlanPhaseProcedureGrid
                                tabKey={tabKey}
                                originalIdx={originalIdx}
                                visibleItems={visibleItems}
                                isEditMode={isEditMode}
                                draggingItemId={draggingItemId}
                                providerList={providerList}
                                providerLookupList={staffList}
                                missingProviderRowKeys={
                                  missingProviderRowKeysByTab[tabKey] ??
                                  EMPTY_MISSING_PROVIDER_KEY_SET
                                }
                                allowManualFeeOverride={allowManualFeeOverride}
                                showInsuranceEstimate={showInsuranceEstimate}
                                onProcedureProviderChange={
                                  handleProcedureProviderChange
                                }
                                onProcedureFeeChange={
                                  allowManualFeeOverride
                                    ? handleProcedureFeeChange
                                    : undefined
                                }
                                setFormStates={setFormStates}
                                setDraggingItemId={setDraggingItemId}
                                interactionRef={phaseGridInteractionRef}
                              />
                            )}
                            {isEditMode && (
                              <TxPlanPhaseAddProcedureRow
                                onAddProcedure={() =>
                                  handleAddProcedure(tabKey, originalIdx)
                                }
                              />
                            )}
                          </div>
                        )
                      })()}
                    </Accordion.Body>
                  </Accordion.Item>
                )
              })}
          </Accordion>
          {isEditMode && (
            <TxPlanAddPhaseRow onAddPhase={() => handleAddPhase(tabKey)} />
          )}
        </div>

        {/* Totals */}
        <div className="create-tx-plan-summary">
          <div className="ctps-title">Plan pricing summary</div>
          <Row>
            <Col xs={6}>
              <div className="tx-label">Total charges:</div>
              {showInsuranceEstimate && (
                <div className="tx-label">
                  Estimated insurance pay
                  <span>
                    {formStates[activeTabKey]?.selectedInsurance?.carrier_name
                      ? ` (${formStates[activeTabKey]?.selectedInsurance?.carrier_name})`
                      : ''}
                  </span>
                  :
                </div>
              )}
              <div className="tx-label">
                Discount
                <span>
                  {(() => {
                    const currentFormState = formStates[activeTabKey]
                    if (!currentFormState) return ''
                    const discountPercent = resolveTxPlanDiscountPercent(
                      currentFormState.selectedDiscountId,
                      currentFormState.customDiscount ?? 0,
                      billingDiscounts,
                    )
                    return discountPercent > 0 ? ` (${discountPercent}%)` : ''
                  })()}
                </span>
                :
              </div>
            </Col>
            <Col xs={6} className="text-end">
              <div className="tx-amount">
                {formatNumber(totals.charges, true)}
              </div>
              {showInsuranceEstimate && (
                <div className="tx-amount">
                  {formatNumber(totals.insurance, true)}
                </div>
              )}
              <div className="tx-amount">
                {formatNumber(totals.adjustments, true)}
              </div>
            </Col>
          </Row>
          {showInsuranceEstimate && (
            <Row className="border-top pt-2 mt-2">
              <Col xs={6} className="tx-label">
                Estimated patient responsibility:
              </Col>
              <Col xs={6} className="text-end tx-amount">
                {formatNumber(totals.outstanding, true)}
              </Col>
            </Row>
          )}
        </div>
      </div>
    )
  }

  const handleCreateNewPlan = useCallback((): void => {
    const currentPatientPlans = selectedPatient
      ? treatmentPlans.filter(
          (p) => p.patient_id === selectedPatient.patient_id,
        )
      : []
    const allExistingNames = [
      ...currentPatientPlans.map((p) => p.tp_heading || ''),
      ...Object.values(formStates)
        .map((fs) => fs?.planName)
        .filter((name): name is string => !!name && name.trim() !== ''),
    ]
    const newPlanName = generateNewTreatmentPlanName(allExistingNames)
    const newPlanId = `draft-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newPhaseKey = `phase-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    // Mark this as a local draft
    setLocalDrafts((prev) => ({ ...prev, [newPlanId]: true }))

    // Create form state for the new plan with an initial phase
    setFormStates((prev) => ({
      ...prev,
      [newPlanId]: {
        planName: newPlanName,
        insuranceProvider: null,
        selectedInsurance: null,
        selectedDiscountId: '',
        customDiscount: 0,
        phases: [
          {
            phase_name: 'Phase 1',
            scheduled_date: null,
            status: 'Proposed',
            items: [],
            phase_id: newPhaseKey,
          },
        ],
        activeAccordionKeys: [newPhaseKey],
        procedureModal: null,
      },
    }))

    // Switch to the new tab and enable edit mode
    setActiveTabKey(newPlanId)
    setEditModes((prev) => ({ ...prev, [newPlanId]: true }))
    // Initialize history for new draft (save initial snapshot after state is set)
    setTimeout(() => {
      saveHistorySnapshot(newPlanId)
    }, 0)
  }, [treatmentPlans, formStates, selectedPatient, saveHistorySnapshot])

  // Expose handleCreateNewPlan via ref - update whenever handleCreateNewPlan changes
  useEffect(() => {
    if (onAddTreatmentPlanRef) {
      onAddTreatmentPlanRef.current = handleCreateNewPlan
    }
    return () => {
      if (onAddTreatmentPlanRef) {
        onAddTreatmentPlanRef.current = null
      }
    }
  }, [onAddTreatmentPlanRef, handleCreateNewPlan])

  const hasNoActivePlans =
    activeTreatmentPlans.length === 0 && Object.keys(localDrafts).length === 0
  const showEmptyPlans = Boolean(
    !chartingLoading && (!selectedPatient || hasNoActivePlans),
  )

  useEffect(() => {
    onEmptyPlansChange?.(showEmptyPlans && Boolean(selectedPatient))
    return () => onEmptyPlansChange?.(false)
  }, [onEmptyPlansChange, selectedPatient, showEmptyPlans])

  const handleStartEditTabName = (
    tabKey: string,
    currentName: string,
  ): void => {
    setEditingTabName(tabKey)
    setEditingTabValue(currentName)
  }

  const handleSaveTabName = (tabKey: string): void => {
    if (editingTabValue.trim() !== '') {
      queueHistorySnapshotBefore(tabKey)
      updateFormState(tabKey, 'planName', editingTabValue.trim())
      markHistorySavePending(tabKey)
    }
    setEditingTabName(null)
    setEditingTabValue('')
  }

  const handleCancelEditTabName = (): void => {
    setEditingTabName(null)
    setEditingTabValue('')
  }

  // Memoize formStatePhases for the active tab to ensure React detects changes
  const memoizedFormStatePhases = useMemo(() => {
    if (!activeTabKey || !formStates[activeTabKey]) return null
    const phases = formStates[activeTabKey].phases
    return phases
  }, [activeTabKey, formStates])

  // Stable reference to the active tab's form state — used as a dep to avoid re-running
  // memos whenever an unrelated tab's state changes.
  const activeTabFormState = formStates[activeTabKey] ?? null

  // Per-tab unsaved-changes flags — skip expensive diff for tabs not in edit mode.
  const tabHasChangesByKey = useMemo(() => {
    const result: Record<string, boolean> = {}
    Object.keys(localDrafts).forEach((draftId) => {
      result[draftId] = true
    })
    activeTreatmentPlans.forEach((plan) => {
      const tabKey = plan.tp_id
      if (result[tabKey]) {
        return
      }
      if (!editModes[tabKey]) {
        result[tabKey] = false
        return
      }
      result[tabKey] = hasFormChanges(tabKey)
    })
    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasFormChanges is a plain fn; its inputs are listed below
  }, [
    activeTreatmentPlans,
    localDrafts,
    formStates,
    editModes,
    treatmentPlans,
    billingDiscounts,
  ])

  // Memoized validation + button-state for the active tab action bar (edit / save / cancel).
  const activeTabActionState = useMemo(() => {
    const isLocalDraft = !!localDrafts[activeTabKey]
    const isExistingPlan = Boolean(
      activeTreatmentPlans.find((p) => p.tp_id === activeTabKey),
    )
    const isEditMode = isLocalDraft || editModes[activeTabKey] || false
    const missingAnyProcedureProvider =
      activeTabFormState !== null &&
      collectTxPlanMissingProviderRowKeys(activeTabFormState.phases).size > 0
    const phaseMissingProcedures =
      activeTabFormState !== null &&
      txPlanHasVisiblePhaseMissingProcedure(activeTabFormState.phases)
    const hasChanges = tabHasChangesByKey[activeTabKey] ?? false
    const saveDisabled =
      !hasChanges && !missingAnyProcedureProvider && !phaseMissingProcedures
    const cancelTitle = isLocalDraft
      ? 'Discard draft'
      : hasChanges
        ? 'Discard changes and exit edit mode'
        : 'Exit edit mode'
    return {
      isLocalDraft,
      isExistingPlan,
      isEditMode,
      missingAnyProcedureProvider,
      phaseMissingProcedures,
      hasChanges,
      saveDisabled,
      cancelTitle,
    }
  }, [
    activeTabKey,
    activeTabFormState,
    localDrafts,
    editModes,
    activeTreatmentPlans,
    tabHasChangesByKey,
  ])

  // Memoized accordion expand/collapse state for the active tab toolbar.
  const activeTabAccordionState = useMemo(() => {
    const visiblePhaseKeys =
      activeTabFormState !== null
        ? collectTxPlanVisiblePhaseAccordionKeys(activeTabFormState.phases)
        : []
    const hasAnyPhaseExpanded =
      activeTabFormState !== null &&
      visiblePhaseKeys.some((key) =>
        activeTabFormState.activeAccordionKeys.includes(key),
      )
    const phaseAccordionToggleDisabled = visiblePhaseKeys.length === 0
    return {
      visiblePhaseKeys,
      hasAnyPhaseExpanded,
      phaseAccordionToggleDisabled,
    }
  }, [activeTabFormState])

  const isChartInteractive = useMemo((): boolean => {
    if (!activeTabKey || activeTabKey === 'create-new') {
      return false
    }
    return Boolean(localDrafts[activeTabKey] || editModes[activeTabKey])
  }, [activeTabKey, editModes, localDrafts])

  const chartSelectedTeethList =
    activeTabKey && activeTabKey !== 'create-new'
      ? (chartSelectedTeeth[activeTabKey] ?? [])
      : []

  const handleChartToothToggle = useCallback(
    (toothId: number): void => {
      if (!activeTabKey || activeTabKey === 'create-new') {
        return
      }
      setChartSelectedTeeth((prev) => {
        const current = prev[activeTabKey] ?? []
        const next = current.includes(toothId)
          ? current.filter((tooth) => tooth !== toothId)
          : [...current, toothId].sort((a, b) => a - b)
        return {
          ...prev,
          [activeTabKey]: next,
        }
      })
    },
    [activeTabKey],
  )

  const handleClearChartSelectedTeeth = useCallback((): void => {
    if (!activeTabKey || activeTabKey === 'create-new') {
      return
    }
    setChartSelectedTeeth((prev) => ({
      ...prev,
      [activeTabKey]: [],
    }))
  }, [activeTabKey])

  const handleAddProceduresForSelectedTooth = (): void => {
    if (!activeTabKey || activeTabKey === 'create-new') {
      return
    }
    if (chartSelectedTeethList.length === 0) {
      showToast(
        createFailedToast(
          'Select a tooth',
          'Click a tooth on the chart before adding procedures.',
        ),
      )
      return
    }
    beginToothDrivenProcedureAdd(activeTabKey, chartSelectedTeethList)
  }

  const isChartInteractiveRef = useRef(isChartInteractive)
  isChartInteractiveRef.current = isChartInteractive
  const activeTabKeyRef = useRef(activeTabKey)
  activeTabKeyRef.current = activeTabKey
  const chartSelectedTeethRef = useRef(chartSelectedTeethList)
  chartSelectedTeethRef.current = chartSelectedTeethList
  const editingTabNameRef = useRef(editingTabName)
  editingTabNameRef.current = editingTabName
  const editingPhaseKeyRef = useRef(editingPhaseKey)
  editingPhaseKeyRef.current = editingPhaseKey
  const pendingToothAddRef = useRef(pendingToothAdd)
  pendingToothAddRef.current = pendingToothAdd
  const beginToothDrivenProcedureAddRef = useRef(beginToothDrivenProcedureAdd)
  beginToothDrivenProcedureAddRef.current = beginToothDrivenProcedureAdd
  const handleAddProcedureRef = useRef(handleAddProcedure)
  handleAddProcedureRef.current = handleAddProcedure
  const handleAddPhaseAndOpenProcedureModalRef = useRef(
    handleAddPhaseAndOpenProcedureModal,
  )
  handleAddPhaseAndOpenProcedureModalRef.current =
    handleAddPhaseAndOpenProcedureModal

  useEffect(() => {
    const isEnterShortcutBlockedTarget = (
      target: EventTarget | null,
    ): boolean => {
      if (!(target instanceof HTMLElement)) return false
      const tag = target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return true
      }
      if (target.isContentEditable) return true
      if (target.closest('.e-popup, .e-dropdown-popup')) return true
      return false
    }

    const handleDocumentKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Enter' || event.repeat) return
      if (event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) {
        return
      }
      if (isEnterShortcutBlockedTarget(event.target)) return
      if (!isChartInteractiveRef.current) return
      const tabKey = activeTabKeyRef.current
      if (!tabKey || tabKey === 'create-new') return
      if (editingTabNameRef.current !== null) return
      if (editingPhaseKeyRef.current !== null) return
      if (pendingToothAddRef.current !== null) return
      if (document.querySelector('.modal.show')) return
      const formState = formStatesRef.current[tabKey]
      if (!formState || formState.procedureModal !== null) return

      event.preventDefault()

      if (chartSelectedTeethRef.current.length > 0) {
        beginToothDrivenProcedureAddRef.current(
          tabKey,
          chartSelectedTeethRef.current,
        )
        return
      }

      const phaseIndex = resolveTxPlanPhaseIndexForProcedureShortcut(
        formState.phases ?? [],
        formState.activeAccordionKeys ?? [],
        phaseVisibilityRef.current,
      )
      if (phaseIndex === null) {
        handleAddPhaseAndOpenProcedureModalRef.current(tabKey)
      } else {
        handleAddProcedureRef.current(tabKey, phaseIndex)
      }
    }

    document.addEventListener('keydown', handleDocumentKeyDown)
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown)
    }
  }, [])

  const pendingToothPhaseOptions = useMemo(() => {
    if (!pendingToothAdd) {
      return []
    }
    const phases = formStates[pendingToothAdd.tabKey]?.phases ?? []
    return getVisibleTxPlanPhasePickerOptions(phases, phaseVisibility)
  }, [pendingToothAdd, formStates, phaseVisibility])

  // Compute chart header name based on treatment plan existence, active tab, and edit state
  const chartHeaderName = useMemo((): string => {
    // If no treatment plans exist, show "Current Chart"
    if (
      activeTreatmentPlans.length === 0 &&
      Object.keys(localDrafts).length === 0
    ) {
      return 'Current chart'
    }

    // If there's an active tab (not 'create-new') OR ongoing edit, show the tab name
    if (
      activeTabKey &&
      activeTabKey !== 'create-new' &&
      (editModes[activeTabKey] || localDrafts[activeTabKey])
    ) {
      const plan =
        activeTreatmentPlans.find((p) => p.tp_id === activeTabKey) ||
        treatmentPlans.find((p) => p.tp_id === activeTabKey)
      const planName =
        plan?.tp_heading ||
        formStates[activeTabKey]?.planName ||
        'Untitled Plan'
      return planName
    }

    // If there's an active tab (not 'create-new'), show the tab name
    if (activeTabKey && activeTabKey !== 'create-new') {
      const plan =
        activeTreatmentPlans.find((p) => p.tp_id === activeTabKey) ||
        treatmentPlans.find((p) => p.tp_id === activeTabKey)
      const planName =
        plan?.tp_heading ||
        formStates[activeTabKey]?.planName ||
        'Untitled Plan'
      return planName
    }

    // Otherwise, show "Current Chart"
    return 'Current chart'
  }, [
    activeTreatmentPlans,
    treatmentPlans,
    localDrafts,
    activeTabKey,
    editModes,
    formStates,
  ])

  // Calculate progress percentage for active treatment plan
  const treatmentPlanProgress = useMemo((): number => {
    if (!activeTabKey || activeTabKey === 'create-new') return 0

    // Get phases from either saved plan or local draft (edit mode)
    const savedPlan = activeTreatmentPlans.find((p) => p.tp_id === activeTabKey)
    const isEditMode = editModes[activeTabKey] || localDrafts[activeTabKey]

    // When in edit mode, prioritize formStates (draft changes), otherwise use saved plan
    const phases = isEditMode
      ? formStates[activeTabKey]?.phases || savedPlan?.phases || []
      : savedPlan?.phases || []

    // Filter out rejected phases
    const validPhases = phases.filter(
      (phase) => phase.status !== TX_PLAN_STATUS.REJECTED,
    )

    // Handle edge cases: no phases or all rejected
    if (validPhases.length === 0) return 0

    // Count completed phases
    const completedPhases = validPhases.filter(
      (phase) => phase.status === TX_PLAN_STATUS.COMPLETED,
    ).length

    // Calculate percentage
    return Math.round((completedPhases / validPhases.length) * 100)
  }, [activeTabKey, activeTreatmentPlans, formStates, editModes, localDrafts])

  const renderTabTitle = (
    plan: ITreatmentPlan | null,
    tabKey: string,
    isActive: boolean,
  ): JSX.Element => {
    if (tabKey === 'create-new') {
      return <span title="Create New Plan">Create New Plan</span>
    }

    if (!plan && !localDrafts[tabKey])
      return <span title="Unknown Plan">Unknown Plan</span>

    const isLocalDraft = Boolean(localDrafts[tabKey])
    const isEditMode = isLocalDraft || Boolean(editModes[tabKey])
    const formPlanName = formStates[tabKey]?.planName
    const planName =
      isEditMode && formPlanName
        ? formPlanName
        : plan?.tp_heading || formPlanName || 'Untitled Plan'
    const showTabRenamePencil = isActive && isEditMode

    // If this tab is being edited, show input
    if (editingTabName === tabKey) {
      return (
        <div
          className="d-flex align-items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            className="form-control form-control-sm"
            style={{
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
              height: '28px',
              padding: '0',
              minWidth: '150px',
              minHeight: 'unset',
              fontSize: '0.875rem',
              color: 'var(--gray-700)',
            }}
            value={editingTabValue}
            onChange={(e) => setEditingTabValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSaveTabName(tabKey)
              } else if (e.key === 'Escape') {
                handleCancelEditTabName()
              } else {
                // Prevent tab navigation from capturing keypresses
                e.stopPropagation()
              }
            }}
            onKeyPress={(e) => {
              // Prevent any key events from bubbling to parent tab navigation
              e.stopPropagation()
            }}
            onBlur={() => handleSaveTabName(tabKey)}
            autoFocus
          />
        </div>
      )
    }

    const hasUnsavedChanges = tabHasChangesByKey[tabKey] ?? false

    return (
      <span className="d-flex align-items-center gap-1" title={planName}>
        {planName}
        {/* Show dot indicator for unsaved changes (only on inactive tabs) */}
        {hasUnsavedChanges && !isActive && (
          <span
            className="treatment-plan-tab-unsaved-dot"
            title="Unsaved changes"
          />
        )}
        {showTabRenamePencil && (
          <i
            className="mgc_pencil_fill treatment-plan-tab-name-edit"
            onClick={(e) => {
              e.stopPropagation()
              handleStartEditTabName(tabKey, planName)
            }}
            title="Rename plan"
          />
        )}
      </span>
    )
  }

  phaseGridInteractionRef.current = {
    saveHistorySnapshot,
    editToothSelection,
    clearTreatmentPlanDragUi,
  }

  return (
    <div
      ref={mainContainerRef}
      className={`treatment-plan-main-container ${chartingLoading ? 'overlay-active' : ''}`}
    >
      {chartingLoading && (
        <div className="treatment-plan-overlay">
          <div className="treatment-plan-loading">
            <Spinner
              animation="border"
              variant="primary"
              role="status"
              className="treatment-plan-loader"
            />
            <p className="treatment-plan-loading-text">
              Loading patient treatment plans...
            </p>
          </div>
        </div>
      )}

      {!selectedPatient ? (
        <EmptyTreatmentPlan description="Select a patient to view their treatment plans." />
      ) : (
        <>
          <TreatmentPlanDentalChartPanel
            chartVisible={chartVisible}
            setChartVisible={setChartVisible}
            chartHeaderName={chartHeaderName}
            activeTabKey={activeTabKey}
            localDrafts={localDrafts}
            editModes={editModes}
            hasFormChanges={hasFormChanges}
            handleResetChart={handleResetChart}
            handleUndo={handleUndo}
            handleRedo={handleRedo}
            canUndo={canUndo}
            canRedo={canRedo}
            showXraySplit={showXraySplit}
            attachTpXraySplitRef={attachTpXraySplitRef}
            tpXrayPaneFraction={tpXrayPaneFraction}
            onTpXraySplitHandleMouseDown={onTpXraySplitHandleMouseDown}
            memoizedFormStatePhases={memoizedFormStatePhases}
            phaseVisibility={phaseVisibility}
            initialEmbeddedFiles={initialEmbeddedFiles}
            isHostVisible={isHostVisible}
            isChartInteractive={isChartInteractive}
            chartSelectedTeeth={chartSelectedTeethList}
            onChartToothToggle={handleChartToothToggle}
            onClearChartSelectedTeeth={handleClearChartSelectedTeeth}
            onAddProceduresForSelectedTooth={
              handleAddProceduresForSelectedTooth
            }
            chartPaneHeightPx={chartPaneHeightPx}
            chartPaneRef={chartPaneRef}
          />
          {chartVisible ? (
            <div
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize dental chart"
              aria-valuenow={chartPaneHeightPx ?? TP_CHART_PANE_MIN_PX}
              aria-valuemin={TP_CHART_PANE_MIN_PX}
              tabIndex={0}
              className="treatment-plan-chart-vertical-handle"
              onMouseDown={onChartVerticalHandleMouseDown}
              style={{ flex: `0 0 ${TP_CHART_VERTICAL_HANDLE_PX}px` }}
            >
              <i
                className="mgc_unfold_vertical_line treatment-plan-chart-vertical-handle__icon"
                aria-hidden
              />
            </div>
          ) : null}
          {hasNoActivePlans ? (
            <div className="treatment-plan-empty-scroll">
              <EmptyTreatmentPlan onCreateNewPlan={handleCreateNewPlan} />
            </div>
          ) : (
            <div className="treatment-plan-workspace">
              <div className="treatment-plan-tabs-container">
                <Tab.Container
                  activeKey={activeTabKey}
                  onSelect={handleTabSelect}
                >
                  <Nav variant="tabs" className="treatment-plan-tabs">
                    <div
                      ref={tabsScrollableRef}
                      className="treatment-plan-tabs-scrollable"
                      onWheel={handleTabsWheel}
                    >
                      {visibleSavedPlans.map((plan) => (
                        <Nav.Item key={plan.tp_id}>
                          <Nav.Link eventKey={plan.tp_id}>
                            {renderTabTitle(
                              plan,
                              plan.tp_id,
                              activeTabKey === plan.tp_id,
                            )}
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                      {/* Render local draft tabs */}
                      {Object.keys(localDrafts).map((draftId) => (
                        <Nav.Item key={draftId}>
                          <Nav.Link eventKey={draftId}>
                            {renderTabTitle(
                              null,
                              draftId,
                              activeTabKey === draftId,
                            )}
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                      {/* Add plan Button */}
                      <Nav.Item>
                        <div
                          className="nav-link"
                          style={{ cursor: 'pointer' }}
                          onClick={handleCreateNewPlan}
                        >
                          <span className="d-flex align-items-center gap-1">
                            <i
                              className="ti ti-plus"
                              style={{ fontSize: '14px' }}
                            />
                            Add plan
                          </span>
                        </div>
                      </Nav.Item>
                    </div>
                    {/* Dropdown button for active tab - show for existing plans and local drafts */}
                    {activeTabKey &&
                      (activeTreatmentPlans.find(
                        (p) => p.tp_id === activeTabKey,
                      ) ||
                        localDrafts[activeTabKey]) && (
                        <Nav.Item className="ms-auto treatment-plan-tab-actions">
                          <div className="d-flex align-items-center gap-1">
                            {/* Progress bar showing treatment plan completion */}
                            <div className="treatment-plan-progress-container">
                              <ProgressBar
                                now={treatmentPlanProgress}
                                style={{ minWidth: '100px', height: '20px' }}
                                className="treatment-plan-progress"
                              />
                              <span className="treatment-plan-progress-label">
                                {treatmentPlanProgress}%
                              </span>
                            </div>
                            {/* Separator after progress bar */}
                            <div className="icon-divider" />
                            <button
                              type="button"
                              className={`icon-wrap perio-voice-mic ${txPlanMicOn ? 'text-danger' : ''}`}
                              disabled={!txPlanVoiceEnabled}
                              onClick={txPlanVoice.toggle}
                              aria-label={
                                txPlanMicOn
                                  ? 'Stop voice assistant'
                                  : 'Start voice assistant'
                              }
                              aria-pressed={txPlanMicOn}
                              title={
                                txPlanVoiceEnabled
                                  ? `Voice assistant: ${txPlanVoice.phase}`
                                  : txPlanVoiceEditing
                                    ? 'Voice assistant is unavailable'
                                    : 'Edit the plan to use voice'
                              }
                            >
                              <i
                                className={
                                  txPlanMicOn ? 'mgc_stop_fill' : 'mgc_mic_fill'
                                }
                              />
                            </button>
                            {/* Only show add note for existing plans */}
                            {activeTreatmentPlans.find(
                              (p) => p.tp_id === activeTabKey,
                            ) && (
                              <Button
                                size="sm"
                                className="icon-wrap"
                                title="Add note"
                                onClick={() => {
                                  const plan = activeTreatmentPlans.find(
                                    (p) => p.tp_id === activeTabKey,
                                  )
                                  if (plan) {
                                    onViewNote(plan.tp_id)
                                  }
                                }}
                              >
                                <i className="mgc_document_fill" />
                              </Button>
                            )}
                            {/* Separator after add note */}
                            <div className="icon-divider" />
                            <>
                              {activeTabActionState.isExistingPlan &&
                                !activeTabActionState.isEditMode && (
                                  <div
                                    className="icon-wrap"
                                    title="Edit plan"
                                    onClick={() => {
                                      enterEditMode(activeTabKey)
                                    }}
                                  >
                                    <i className="mgc_pencil_fill" />
                                  </div>
                                )}
                              {activeTabActionState.isEditMode && (
                                <>
                                  <div
                                    className={`icon-wrap icon-wrap-blue ${activeTabActionState.saveDisabled ? 'disabled pe-none' : ''}`}
                                    title={
                                      activeTabActionState.saveDisabled
                                        ? 'No changes to save'
                                        : 'Save changes'
                                    }
                                    onClick={() => {
                                      if (!activeTabActionState.saveDisabled) {
                                        handleSave(activeTabKey, false)
                                      }
                                    }}
                                  >
                                    <i className="mgc_save_2_line" />
                                  </div>
                                  <div
                                    className="icon-wrap"
                                    title={activeTabActionState.cancelTitle}
                                    onClick={() => {
                                      requestDiscardPlanEdits(activeTabKey)
                                    }}
                                  >
                                    <i className="mgc_close_line" />
                                  </div>
                                </>
                              )}
                            </>
                            <div className="icon-divider" />
                            <div
                              className={`icon-wrap ${activeTabAccordionState.phaseAccordionToggleDisabled ? 'disabled pe-none' : ''}`}
                              title={
                                activeTabAccordionState.phaseAccordionToggleDisabled
                                  ? 'No phases to expand or collapse'
                                  : activeTabAccordionState.hasAnyPhaseExpanded
                                    ? 'Collapse all phases'
                                    : 'Expand all phases'
                              }
                              onClick={() => {
                                if (
                                  !activeTabAccordionState.phaseAccordionToggleDisabled
                                ) {
                                  toggleAllPhaseAccordions(activeTabKey)
                                }
                              }}
                            >
                              <i
                                className={`mgc ${activeTabAccordionState.hasAnyPhaseExpanded ? 'mgc_list_collapse_line' : 'mgc_list_expansion_line'}`}
                                aria-hidden
                              />
                            </div>
                            <PlanActionMenuButton
                              tabKey={activeTabKey}
                              isLocalDraft={!!localDrafts[activeTabKey]}
                              providerMenuDisabled={providerMenuDisabled}
                              onProviderMenuSelect={handleProviderMenuSelect}
                              insuranceList={insuranceList}
                              selectedInsurance={
                                formStates[activeTabKey]?.selectedInsurance ||
                                null
                              }
                              billingDiscounts={activeBillingDiscounts}
                              selectedDiscountId={
                                formStates[activeTabKey]?.selectedDiscountId ??
                                ''
                              }
                              customDiscount={
                                formStates[activeTabKey]?.customDiscount || 0
                              }
                              treatmentPlans={treatmentPlans}
                              onMenuSelect={handlePlanMenuSelect}
                              onInsuranceSelect={handleInsuranceSelect}
                              onDiscountSelect={handleDiscountSelect}
                              onCustomDiscountChange={
                                handleCustomDiscountChange
                              }
                              onDuplicatePlan={handleDuplicatePlan}
                            />
                          </div>
                        </Nav.Item>
                      )}
                  </Nav>
                  <Tab.Content>
                    {visibleSavedPlans.map((plan) => (
                      <Tab.Pane eventKey={plan.tp_id} key={plan.tp_id}>
                        {activeTabKey === plan.tp_id
                          ? renderTreatmentPlanForm(plan.tp_id, plan)
                          : null}
                      </Tab.Pane>
                    ))}
                    {Object.keys(localDrafts).map((draftId) => (
                      <Tab.Pane eventKey={draftId} key={draftId}>
                        {activeTabKey === draftId
                          ? renderTreatmentPlanForm(draftId, null)
                          : null}
                      </Tab.Pane>
                    ))}
                  </Tab.Content>
                </Tab.Container>
              </div>
              {txPlanVoice.phase !== 'idle' ? (
                <TxPlanVoiceStatus
                  voice={txPlanVoice}
                  procedures={voiceProcedures}
                />
              ) : null}
            </div>
          )}
        </>
      )}

      {/* Procedure Modal */}
      {Object.keys(formStates).map((tabKey) => {
        const formState = formStates[tabKey]
        const procedureModalCtx = formState?.procedureModal ?? null
        if (!formState || procedureModalCtx === null) return null

        return (
          <AddTxPlanProcedureModal
            key={`procedure-modal-${tabKey}-${procedureModalCtx.phaseIndex}-${
              (procedureModalCtx.presetToothDetails ?? [])
                .map((detail) => detail.tooth_num)
                .filter(
                  (toothNum): toothNum is number => toothNum !== undefined,
                )
                .join('-') || 'none'
            }`}
            show={true}
            presetToothDetails={procedureModalCtx.presetToothDetails}
            initialQuery={procedureModalCtx.initialQuery}
            onHide={() => {
              updateFormState(tabKey, 'procedureModal', null)
            }}
            selectedInsurance={formState.selectedInsurance}
            discountPercent={resolveTxPlanDiscountPercent(
              formState.selectedDiscountId,
              formState.customDiscount,
              billingDiscounts,
            )}
            insuranceList={insuranceList}
            patientInsurancePlans={
              selectedPatient
                ? (selectedPatient as unknown as PatientWithInsurance)
                    .insurance_plans
                : undefined
            }
            onSelectProcedure={(procedureItem: IPhaseItems) => {
              if (procedureModalCtx === null) return

              queueHistorySnapshotBefore(tabKey)
              try {
                const charges =
                  procedureItem.charges && procedureItem.charges > 0
                    ? procedureItem.charges
                    : Number(procedureItem.procedure_settings?.clinic_fee ?? 0)
                const insuranceAmt =
                  procedureItem.insurance && procedureItem.insurance > 0
                    ? procedureItem.insurance
                    : calculateInsuranceForItem(
                        { ...procedureItem, charges },
                        formState.selectedInsurance,
                      )
                // Use adjustments from procedureItem if available, otherwise calculate
                const adjustmentAmt =
                  procedureItem.adjustments !== undefined
                    ? procedureItem.adjustments
                    : ((charges - insuranceAmt) *
                        resolveTxPlanDiscountPercent(
                          formState.selectedDiscountId,
                          formState.customDiscount,
                          billingDiscounts,
                        )) /
                      100
                // Use outstanding from procedureItem if available, otherwise calculate
                const outstandingAmt =
                  procedureItem.outstanding !== undefined
                    ? procedureItem.outstanding
                    : charges - insuranceAmt - adjustmentAmt

                // Extract default material and type from resolved dental conditions
                type ProcedureWithDentalConditions = Procedure & {
                  procedure_dental_conditions?: IProcedureDentalCondition[]
                }
                const procedureWithDentalConditions = procedures.find(
                  (p) => p.procedure_id === procedureItem.procedure_id,
                ) as ProcedureWithDentalConditions | undefined
                const resolvedProcedureDentalConditions =
                  resolveProcedureDentalConditionsWithNestedCondition(
                    procedureWithDentalConditions?.procedure_dental_conditions,
                    procedureItem.proceduredentalcondition,
                    procedureItem.procedure_settings?.procedure
                      ?.procedure_dental_conditions,
                  )
                const defaultMaterial =
                  resolvedProcedureDentalConditions[0]?.default_material ?? null
                const defaultType =
                  resolvedProcedureDentalConditions[0]?.default_type ?? null

                const enhancedProcedureSettings =
                  procedureItem.procedure_settings
                    ? {
                        ...procedureItem.procedure_settings,
                        procedure: procedureItem.procedure_settings.procedure
                          ? {
                              ...procedureItem.procedure_settings.procedure,
                              procedure_dental_conditions:
                                resolvedProcedureDentalConditions,
                            }
                          : procedureWithDentalConditions
                            ? {
                                procedure_id:
                                  procedureWithDentalConditions.procedure_id,
                                procedure_code:
                                  procedureWithDentalConditions.procedure_code,
                                description:
                                  procedureWithDentalConditions.description,
                                procedure_dental_conditions:
                                  resolvedProcedureDentalConditions,
                              }
                            : undefined,
                      }
                    : undefined

                const phaseIndex = procedureModalCtx.phaseIndex
                const phaseBeforeAdd = formStates[tabKey]?.phases[phaseIndex]
                const phaseKeyToClear =
                  phaseBeforeAdd !== undefined
                    ? txPlanPhaseHeaderKey(phaseBeforeAdd, phaseIndex)
                    : null
                setFormStates((prev) => {
                  const prevState = prev[tabKey]
                  if (!prevState) return prev
                  return {
                    ...prev,
                    [tabKey]: {
                      ...prevState,
                      phases: prevState.phases.map((phase, idx) =>
                        idx === phaseIndex
                          ? {
                              ...phase,
                              items: [
                                ...phase.items,
                                {
                                  ...procedureItem,
                                  item_id:
                                    procedureItem.item_id &&
                                    procedureItem.item_id.length > 0
                                      ? procedureItem.item_id
                                      : `item-${Date.now()}-${Math.random()
                                          .toString(36)
                                          .substr(2, 9)}`,
                                  phase_id: phase.phase_id ?? null,
                                  provider_id: '',
                                  charges,
                                  insurance: insuranceAmt,
                                  adjustments: adjustmentAmt,
                                  outstanding: outstandingAmt,
                                  // Preserve allowed_amount and write_off from modal if available
                                  allowed_amount:
                                    procedureItem.allowed_amount ?? null,
                                  write_off: procedureItem.write_off ?? null,
                                  material: defaultMaterial,
                                  type: defaultType,
                                  proceduredentalcondition:
                                    resolvedProcedureDentalConditions.length > 0
                                      ? resolvedProcedureDentalConditions
                                      : undefined,
                                  procedure_settings: enhancedProcedureSettings,
                                },
                              ],
                            }
                          : phase,
                      ),
                      procedureModal: null,
                    },
                  }
                })
                if (phaseKeyToClear !== null) {
                  clearPhaseValidationError(tabKey, phaseKeyToClear)
                }
                setChartSelectedTeeth((prev) => ({
                  ...prev,
                  [tabKey]: [],
                }))
                // Mark that we need to save history after state update for redo (this is the "after" state)
                markHistorySavePending(tabKey)
              } catch (error) {
                logger.error('Error adding procedure to phase:', error)
                updateFormState(tabKey, 'procedureModal', null)
              }
            }}
          />
        )
      })}

      {pendingToothAdd && (
        <TxPlanToothPhasePicker
          show={pendingToothPhaseOptions.length > 0}
          toothNums={pendingToothAdd.toothNums}
          phases={pendingToothPhaseOptions}
          onHide={() => setPendingToothAdd(null)}
          onSelectPhase={(phaseIndex) => {
            handleAddProcedureFromTooth(
              pendingToothAdd.tabKey,
              pendingToothAdd.toothNums,
              phaseIndex,
            )
            setPendingToothAdd(null)
          }}
          onAddNewPhase={() => {
            handleAddNewPhaseFromToothPicker(
              pendingToothAdd.tabKey,
              pendingToothAdd.toothNums,
            )
          }}
        />
      )}

      <ChartingChangePhaseStatusModal
        show={!!pendingStatusChange}
        phaseName={pendingStatusChange?.phaseName ?? ''}
        newStatus={pendingStatusChange?.newStatus ?? 'Proposed'}
        onCancel={() => setPendingStatusChange(null)}
        onConfirm={confirmStatusChange}
      />

      <ChartingDiscardPlanChangesModal
        show={!!pendingDiscard}
        planName={pendingDiscard?.planName ?? ''}
        onDismiss={() => setPendingDiscard(null)}
        onSaveChanges={() => {
          if (!pendingDiscard) return
          const tabKey = pendingDiscard.tabKey
          setPendingDiscard(null)
          handleSave(tabKey, false)
        }}
        onDiscard={confirmDiscardPlanEdits}
      />

      <ChartingDeleteItemModal
        show={!!pendingDelete}
        itemName={pendingDelete?.treatmentPlanName || 'Untitled Plan'}
        itemKind="treatmentPlan"
        onCancel={() => setPendingDelete(null)}
        onDelete={confirmDeleteTreatmentPlan}
      />

      <TreatmentPlanProviderModals
        planProviderAssignModal={planProviderAssignModal}
        menuFillProviderId={menuFillProviderId}
        onMenuFillProviderIdChange={setMenuFillProviderId}
        onClosePlanProviderAssign={closePlanProviderAssignModal}
        onConfirmPlanProviderAssign={confirmPlanProviderAssign}
        clearPlanProvidersModal={clearPlanProvidersModal}
        onCloseClearPlanProviders={closeClearPlanProvidersModal}
        onConfirmClearPlanProviders={confirmClearPlanProviders}
        providerList={providerList}
        pendingBulkProvider={pendingBulkProvider}
        bulkProviderDontAskAgain={bulkProviderDontAskAgain}
        onBulkProviderDontAskAgainChange={setBulkProviderDontAskAgain}
        onBulkProviderModalHide={handleBulkProviderModalHide}
        onBulkProviderOnlyThisProcedure={handleBulkProviderOnlyThisProcedure}
        onConfirmBulkProviderApplyAll={confirmBulkProviderApplyAll}
      />

      <ChartingDeleteItemModal
        show={!!pendingPhaseDelete}
        itemName={pendingPhaseDelete?.phase.phase_name || 'Untitled Phase'}
        itemKind="phase"
        onCancel={() => setPendingPhaseDelete(null)}
        onDelete={confirmDeletePhase}
      />

      {/* Tooth Selection Modal */}
      <ApptToothModal
        isOpen={isToothModalOpen}
        closeModal={closeToothModal}
        saveToothSelection={saveToothSelection}
        skipToothSelection={skipToothSelection}
        secondaryActionLabel="Cancel"
        initialToothSelections={initialToothSelectionsForEdit}
        procedureSurfaceSelection={toothEditProcedureSurfaceSelection}
        procedureSurfaceFilterText={
          selectedItemForToothEdit
            ? (selectedItemForToothEdit.item.procedure_settings?.procedure
                ?.description ??
              selectedItemForToothEdit.item.procedure_settings?.description ??
              null)
            : null
        }
      />
    </div>
  )
}

export default TreatmentPlanMain
